/*
  MoonSwatch strap bundle page, collection edition.

  One page serves every watch. The section hands over every candidate strap in
  the collection, already filtered to the watch family's lug width; everything
  below decides which listing to SHOW for the chosen watch, and prices the
  bundle.

  The three derivations that make this work, none of which need the catalogue
  edited:

    what fits      the lug width, done in Liquid before this file sees a row
    which listing  the mission parsed out of the product title
    same strap     a key of material marker plus bare colour, so the mission
                   listing's "Vertech Black" and the generic listing's
                   "Curved Vertech Rubber Strap - Black" collapse to one tile

  Preference order per strap: the chosen watch's own listing first, because its
  variant photograph is that watch wearing that strap, then the generic listing.

  Title parsing is the fragile joint. Titles are inconsistent ("Mission To" and
  "Mission to", "The Moonphase" and "Moonphase"), so a custom.mission metafield
  would make this exact. Until then everything here fails soft: an unparsed
  title is treated as generic, which shows the strap rather than hiding it.
*/

(function () {
  'use strict';

  var MIN_SLOTS = 4;
  var STORAGE_KEY = 'wb-bundle-watch';
  var BUNDLE_KEY = 'wb-bundle-straps';
  /*
    Shaped like the panel it stands in for: one closure tile, a row of finishes,
    one more tile. Static rather than shimmering, because it is usually on
    screen for a few hundred milliseconds and a pulse in that time reads as a
    glitch rather than as loading.
  */
  var CLOSURE_SKELETON =
    '<div class="bundle-closure-skeleton" aria-hidden="true">' +
    '<span class="bundle-closure-skeleton__tile"></span>' +
    '<span class="bundle-closure-skeleton__row">' +
    '<span></span><span></span><span></span><span></span>' +
    '</span>' +
    '<span class="bundle-closure-skeleton__tile"></span>' +
    '</div>';
  // Long enough to survive a reload, a phone call or a look at another tab.
  // Not long enough that yesterday's half-built bundle reappears as a surprise.
  var BUNDLE_TTL_MS = 60 * 60 * 1000;

  function formatMoney(cents, format) {
    var amount = cents / 100;
    function delim(value, decimals, thousands, decimal) {
      var parts = value.toFixed(decimals).split('.');
      var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts[1] ? whole + decimal + parts[1] : whole;
    }
    return (format || '{{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, name) {
      switch (name) {
        case 'amount_no_decimals': return delim(amount, 0, ',', '.');
        case 'amount_with_comma_separator': return delim(amount, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return delim(amount, 0, '.', ',');
        case 'amount_with_space_separator': return delim(amount, 2, ' ', ',');
        case 'amount_no_decimals_with_space_separator': return delim(amount, 0, ' ', ',');
        default: return delim(amount, 2, ',', '.');
      }
    });
  }

  /*
    Every material rolls up to one of two, because a shopper choosing a strap is
    choosing rubber or leather. Vertech and FKM are rubber; suede, epsom and
    calfskin are leather. The specific name still labels the row, it just does
    not get its own filter.

    `group` is what the chips filter on. `key` stays distinct so a listing can
    still say what it actually is.
  */
  var MATERIALS = [
    { key: 'calfskin', label: 'Calfskin', group: 'leather', test: /calfskin/i },
    { key: 'suede', label: 'Suede', group: 'leather', test: /suede/i },
    { key: 'epsom', label: 'Epsom', group: 'leather', test: /epsom/i },
    { key: 'denim', label: 'Denim', group: 'leather', test: /denim/i },
    { key: 'leather', label: 'Leather', group: 'leather', test: /leather/i },
    { key: 'fkm', label: 'FKM rubber', group: 'rubber', test: /\bfkm\b/i },
    { key: 'vertech', label: 'Vertech rubber', group: 'rubber', test: /vertech/i },
    { key: 'nato', label: 'NATO', group: 'rubber', test: /\bnato\b/i },
    { key: 'rubber', label: 'Rubber', group: 'rubber', test: /rubber|silicone/i },
  ];

  var MATERIAL_GROUPS = [
    { key: 'rubber', label: 'Rubber' },
    { key: 'leather', label: 'Leather' },
  ];

  /** The material marker, read from the title and the colour together. */
  function materialOf(title, colour) {
    var haystack = String(title || '') + ' ' + String(colour || '');
    for (var i = 0; i < MATERIALS.length; i += 1) {
      if (MATERIALS[i].test.test(haystack)) return MATERIALS[i];
    }
    return { key: 'other', label: 'Other', group: 'rubber', test: null };
  }

  var NOISE = /\b(curved|integrated|premium|standard|vertech|fkm|rubber|silicone|leather|suede|epsom|calfskin|nato|strap|straps|for|with|original|clasp|omega|x|swatch|moonswatch|mission|to|on|the|and|mm)\b/gi;

  /** Colour with the model and material words taken out, so two listings match. */
  function bareColour(colour) {
    return String(colour || '')
      .replace(NOISE, ' ')
      .replace(/[^a-z0-9&]+/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  /**
   * The mission a listing is cut for, or null when it is generic.
   *
   * "Mission to Earthphase - Moonshine Gold Cold Moon" keeps its whole tail,
   * because that is a different watch from plain Earthphase.
   */
  function missionOf(title) {
    var t = String(title || '').replace(/\s+/g, ' ').trim();
    var m = t.match(/mission\s+(?:to|on)\s+(.+)$/i);
    if (m) return tidyMission(m[1]);
    m = t.match(/moonswatch\s+((?:19|20)\d{2})\b/i);
    if (m) return tidyMission(m[1]);
    return null;
  }

  function tidyMission(name) {
    return String(name)
      // Escaped rather than literal so the en and em dash do not sit in source.
      .replace(/[\u2013\u2014]/g, '-')
      .replace(/\s+/g, ' ')
      .replace(/^the\s+/i, '')
      .replace(/\s*[-,]\s*wristbuddys.*$/i, '')
      .trim();
  }

  function missionKey(name) {
    return String(name || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
  }

  /**
   * The same name, readable, for the URL.
   *
   * missionKey strips every separator because it exists to match titles that
   * punctuate the same watch differently. That makes a fine internal key and a
   * terrible link: ?watch=earthphasemoonshinegoldcoldmoon. This keeps the
   * dashes, and since missionKey is what reads the parameter back, both spellings
   * still resolve, so links already sent out keep working.
   */
  function missionSlug(name) {
    return String(name || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  /* ---------------------------------------------------------------- model */

  function Catalogue(rows) {
    this.straps = {};   // strap key -> { key, material, colour, listings: {missionKey|'' : row} }
    this.missions = {}; // mission key -> { name, colourways }
    this.allRows = rows;

    rows.forEach(function (row) {
      // Leftover test products would otherwise appear as selectable watches:
      // the catalogue carries an "Earthphase - Moonshine Gold Cold Moon TEST".
      if (/\btest\b/i.test(row.t)) return;

      var mission = missionOf(row.t);
      var material = materialOf(row.t, row.c);
      var colour = bareColour(row.c);
      if (!colour) colour = bareColour(row.t) || 'default';

      var key = material.key + '|' + colour;
      var strap = this.straps[key];
      if (!strap) {
        strap = this.straps[key] = {
          key: key,
          material: material,
          colour: row.c,
          listings: {},
        };
      }

      var slot = mission ? missionKey(mission) : '';
      // First listing for a mission wins; later duplicates of the same mission
      // and colour are the redundant listings this key exists to collapse.
      if (!strap.listings[slot]) strap.listings[slot] = row;

      if (mission) {
        var mk = missionKey(mission);
        if (!this.missions[mk]) this.missions[mk] = { key: mk, name: mission, colourways: 0, thumb: row.img };
        this.missions[mk].colourways += 1;
      }
    }, this);
  }

  /** The row to show for this strap on this watch, or null if there is none. */
  Catalogue.prototype.rowFor = function (strap, watchKey) {
    return strap.listings[watchKey] || strap.listings[''] || null;
  };

  Catalogue.prototype.missionList = function () {
    var out = [];
    Object.keys(this.missions).forEach(function (k) { out.push(this.missions[k]); }, this);
    return out.sort(function (a, b) {
      return b.colourways - a.colourways || a.name.localeCompare(b.name);
    });
  };

  /* ---------------------------------------------------------------- page */

  var COLOUR_NAMES = ['color', 'colour', 'recommended colors', 'recommended colours', 'farbe', 'färg'];
  var LUG_NAMES = ['lug width', 'lug', 'bandanstoss'];
  var LENGTH_NAMES = ['strap length', 'length', 'size', 'storlek'];

  function indexOfOption(product, names) {
    var options = product.options || [];
    for (var i = 0; i < options.length; i += 1) {
      var name = String(options[i].name || options[i]).toLowerCase().trim();
      if (names.indexOf(name) !== -1) return i;
    }
    return -1;
  }

  /**
   * Turn the storefront JSON into candidate rows: one per listing and colour,
   * already filtered to this watch family's lug width.
   *
   * The lug width is the answer to "does this fit", so a listing offering lug
   * widths that do not include ours is dropped whole, and a listing with no lug
   * option at all is MoonSwatch-specific and kept.
   */
  function rowsFromProducts(products, lug) {
    var rows = [];
    products.forEach(function (product) {
      var ci = indexOfOption(product, COLOUR_NAMES);
      var li = indexOfOption(product, LUG_NAMES);
      var si = indexOfOption(product, LENGTH_NAMES);

      if (li !== -1) {
        var offered = (product.options[li].values || []).some(function (v) {
          return String(v).indexOf(lug) !== -1;
        });
        if (!offered) return;
      }

      /*
        Which photographs belong to which colourway.

        Shopify's convention, and the one this catalogue follows: an image
        assigned to a variant begins that colourway's run, and every unassigned
        image after it belongs to the same colourway until the next assignment.
        Mercury has 58 images and 15 assignments, so each colourway has four or
        five photographs rather than the one its assignment names.

        Keyed by colour, not by variant. A listing sold across lug widths and
        lengths has many variants per colour and assigns its image to only one
        of them, so keying by variant left the row's own variant with nothing:
        black-suede-leather-strap-18-21mm has 3 images, 10 variants, 1 colour
        and 1 assignment, and came out with a single photograph.

        A single-colour listing takes all of its images regardless of position,
        since there is no other colourway they could belong to.
      */
      var colourOf = {};
      var distinctColours = {};
      (product.variants || []).forEach(function (variant) {
        var opts = [variant.option1, variant.option2, variant.option3];
        var colour = ci !== -1 ? opts[ci] : variant.title;
        colourOf[variant.id] = colour;
        distinctColours[colour] = true;
      });

      var ordered = (product.images || []).slice().sort(function (a, b) {
        return (a.position || 0) - (b.position || 0);
      });

      var shotsByColour = {};
      var soleColour = Object.keys(distinctColours);

      if (soleColour.length === 1) {
        shotsByColour[soleColour[0]] = ordered.map(function (image) {
          return String(image.src || image);
        });
      } else {
        var run = null;
        ordered.forEach(function (image) {
          var ids = image.variant_ids || [];
          if (ids.length) {
            var started = colourOf[ids[0]];
            if (started !== undefined) run = started;
            ids.forEach(function (id) {
              var colour = colourOf[id];
              if (colour === undefined) return;
              if (!shotsByColour[colour]) shotsByColour[colour] = [];
              shotsByColour[colour].push(String(image.src || image));
            });
            return;
          }
          // Unassigned: part of the run it follows. Anything before the first
          // assignment belongs to no colourway and is left out.
          if (run === null) return;
          if (!shotsByColour[run]) shotsByColour[run] = [];
          shotsByColour[run].push(String(image.src || image));
        });
      }

      var byColour = {};
      (product.variants || []).forEach(function (variant) {
        var opts = [variant.option1, variant.option2, variant.option3];
        if (li !== -1 && String(opts[li] || '').indexOf(lug) === -1) return;

        var colour = ci !== -1 ? opts[ci] : variant.title;
        var key = String(colour || '').toLowerCase();
        var length = si !== -1 ? opts[si] : '';

        var image = (variant.featured_image && variant.featured_image.src) ||
          (product.images && product.images[0] && (product.images[0].src || product.images[0])) || '';

        var row = byColour[key];
        if (!row) {
          row = byColour[key] = {
            t: product.title,
            h: product.handle,
            v: variant.id,
            c: colour || product.title,
            p: Math.round(parseFloat(variant.price) * 100) || 0,
            a: false,
            // Whether v/p currently name an available variant, not just any.
            va: variant.available !== false,
            src: image ? String(image) : '',
            img: sized(image, 400),
            // Real photographs of this colourway only, never a padded list.
            shots: (shotsByColour[colour] || []).slice(),
            // Every length offered for this colour at this lug width. The first
            // version kept only the first variant, which threw the sizes away
            // and left the sheet with a Size label and nothing under it.
            lengths: [],
          };
          rows.push(row);
        }
        if (variant.available !== false) row.a = true;
        if (!row.shots.length && image) row.shots.push(String(image));
        (shotsByColour[colour] || []).forEach(function (src) {
          if (row.shots.indexOf(src) === -1) row.shots.push(src);
        });
        if (length && !row.lengths.some(function (l) { return l.value === length; })) {
          row.lengths.push({ value: length, variantId: variant.id, available: variant.available !== false });
        }
        /*
          The cheapest available variant sets the tile, and an unavailable seed
          is replaced by the first available one whatever it costs.

          The seed is simply the colour's first variant, so it can be sold out,
          and this only ever replaced it with something strictly cheaper. A
          colourway whose first length had sold out therefore kept that length
          as the tile's variant and as its price, and /cart/add.js answered 422
          for the whole batch.
        */
        var cents = Math.round(parseFloat(variant.price) * 100) || 0;
        if (variant.available !== false && cents && (!row.va || cents < row.p)) {
          row.p = cents;
          row.v = variant.id;
          row.va = true;
        }
      });
    });
    return rows;
  }

  /**
   * Whether two Shopify CDN URLs point at the same underlying file.
   *
   * Compares the filename with the width token and the query stripped, so
   * foo.jpg?v=1&width=400 and foo_400x.jpg?v=1 read as the same photograph.
   */
  function samePhoto(a, b) {
    var strip = function (u) {
      return String(u || '')
        .split('?')[0]
        .split('/').pop()
        .replace(/_(\d+x\d*|x\d+)(?=\.[a-z]+$)/i, '');
    };
    var left = strip(a);
    return !!left && left === strip(b);
  }

  /** A Shopify CDN URL at a given width. */
  function sized(src, width) {
    if (!src) return '';
    return String(src).replace(/(\.[a-z]+)(\?|$)/i, '_' + width + 'x$1$2');
  }

  /**
   * The closure map, page by page.
   *
   * collection.products is capped at 50 per page by the collection object, so
   * this walks pages until the section says there is no next one.
   */
  function fetchClosureMap(url, productCount) {
    if (!url) return Promise.resolve(null);

    /*
      Every page at once. Walking them one at a time cost a round trip each and
      the grid was waiting on all of them, which is most of why the tiles took
      2.6s to appear. The page count comes from the collection's own total, so
      there is nothing to discover by asking.
    */
    var pages = Math.max(1, Math.ceil((productCount || 50) / 50));
    var sep = url.indexOf('?') === -1 ? '?' : '&';
    var work = [];
    for (var n = 1; n <= Math.min(pages, 12); n += 1) {
      work.push(fetch(url + sep + 'page=' + n, { headers: { Accept: 'text/html' } })
        .then(function (r) {
          if (!r.ok) throw new Error('closure map ' + r.status);
          return r.text();
        })
        .then(function (text) {
          var open = text.indexOf('{');
          var close = text.lastIndexOf('}');
          if (open === -1 || close === -1) throw new Error('closure map was not JSON');
          return JSON.parse(text.slice(open, close + 1));
        }));
    }

    return Promise.all(work).then(function (bodies) {
      var merged = { products: {}, overrides: {} };
      bodies.forEach(function (body) {
        Object.keys(body.products || {}).forEach(function (k) { merged.products[k] = body.products[k]; });
        Object.keys(body.overrides || {}).forEach(function (k) { merged.overrides[k] = body.overrides[k]; });
      });
      return merged;
    });
  }

  /** Every page of the collection's JSON, so nothing is cut off at 50. */
  function fetchAllProducts(url) {
    var all = [];
    function page(n) {
      var sep = url.indexOf('?') === -1 ? '?' : '&';
      return fetch(url + sep + 'limit=250&page=' + n, { headers: { Accept: 'application/json' } })
        .then(function (r) {
          if (!r.ok) throw new Error('collection JSON ' + r.status);
          return r.json();
        })
        .then(function (body) {
          var batch = (body && body.products) || [];
          all = all.concat(batch);
          if (batch.length === 250 && n < 8) return page(n + 1);
          return all;
        });
    }
    return page(1);
  }

  function Bundle(root) {
    this.root = root;
    this.data = JSON.parse(root.querySelector('[data-bundle-data]').textContent);
    this.catalogue = new Catalogue([]);
    this.query = '';
    this.straps = [];
    this.pending = null;
    this.material = 'all';

    this.el = {
      grid: root.querySelector('[data-bundle-grid]'),
      empty: root.querySelector('[data-bundle-empty]'),
      materials: root.querySelector('[data-bundle-materials]'),
      panel: root.querySelector('[data-bundle-bar]'),
      handle: root.querySelector('[data-bundle-handle]'),
      thumbs: root.querySelector('[data-bundle-thumbs]'),
      total: root.querySelector('[data-bundle-total]'),
      list: root.querySelector('[data-bundle-list]'),
      submit: root.querySelector('[data-bundle-submit]'),
      title: root.querySelector('[data-bundle-title]'),
      watchName: root.querySelector('[data-bundle-watch-name]'),
      watchThumb: root.querySelector('[data-bundle-watch-thumb]'),
      fit: root.querySelector('[data-bundle-fit]'),
      sheet: root.querySelector('[data-bundle-sheet]'),
      sheetName: root.querySelector('[data-bundle-sheet-name]'),
      sheetPrice: root.querySelector('[data-bundle-sheet-price]'),
      sheetThumb: root.querySelector('[data-bundle-sheet-thumb]'),
      sizeField: root.querySelector('[data-bundle-size-field]'),
      sizes: root.querySelector('[data-bundle-sizes]'),
      sizeGuide: root.querySelector('[data-bundle-size-guide]'),
      closureHost: root.querySelector('[data-bundle-closure-host]'),
      confirm: root.querySelector('[data-bundle-sheet-confirm]'),
      watchSheet: root.querySelector('[data-bundle-watch-sheet]'),
      watchList: root.querySelector('[data-bundle-watch-list]'),
      watchSearch: root.querySelector('[data-bundle-watch-search]'),
      viewer: root.querySelector('[data-bundle-viewer]'),
      viewerName: root.querySelector('[data-bundle-viewer-name]'),
      viewerOn: root.querySelector('[data-bundle-viewer-on]'),
      viewerPhoto: root.querySelector('[data-bundle-viewer-photo]'),
      viewerDots: root.querySelector('[data-bundle-viewer-dots]'),
      viewerStrip: root.querySelector('[data-bundle-viewer-strip]'),
      viewerFootName: root.querySelector('[data-bundle-viewer-foot-name]'),
      viewerMeta: root.querySelector('[data-bundle-viewer-meta]'),
      viewerWas: root.querySelector('[data-bundle-viewer-was]'),
      viewerNow: root.querySelector('[data-bundle-viewer-now]'),
      viewerAdd: root.querySelector('[data-bundle-viewer-add]'),
    };

    this.expanded = false;
    this.bind();
    this.load();
  }

  /** Shell first, then the catalogue. */
  /**
   * The catalogue first, the closure map behind it.
   *
   * These used to be awaited together, so the grid waited on the map's pages as
   * well as its own fetch. The map is only needed to group the closure panels,
   * which nothing looks at until a sheet opens, so it no longer holds the tiles
   * back.
   */
  Bundle.prototype.load = function () {
    var self = this;

    var map = fetchClosureMap(this.data.closureMapUrl, this.data.allProductsCount)
      .then(function (result) {
        self.closureMap = result;
        if (self.catalogue && self.straps && self.straps.length) self.renderThumbs();
        /*
          Only now can the panels be grouped by signature, and this is already
          well after first paint, so there is nothing left to stay out of the
          way of. It used to sit behind requestIdleCallback with a three second
          timeout, which a shopper tapping straight into a strap could easily
          beat: the sheet then opened short and grew as the panel arrived.
        */
        var start = function () { self.prefetchClosures(); };
        if (window.requestIdleCallback) window.requestIdleCallback(start, { timeout: 250 });
        else window.setTimeout(start, 0);
      })
      .catch(function () { self.closureMap = null; });

    fetchAllProducts(this.data.productsUrl)
      .then(function (products) {
        self.catalogue = new Catalogue(rowsFromProducts(products, self.data.lugWidth));
        self.allRows = self.catalogue.allRows;
        self.watch = self.initialWatch();
        self.renderWatch();
        self.renderMaterials();
        self.rebuild();
        // Needs the catalogue to look rows up again, and the watch to label them.
        self.restoreStraps();
        return map;
      })
      .catch(function (error) {
        self.el.empty.hidden = false;
        if (window.slog) {
          window.slog('custom', { message: 'bundle collection could not load its catalogue', extra: { error: String(error) } });
        }
      });
  };

  Bundle.prototype.money = function (cents) {
    return formatMoney(cents, this.data.moneyFormat);
  };

  /**
   * The remembered watch, as { name, thumb }.
   *
   * The section's own inline script reads this same key during parse so the
   * watch bar can be painted before this file has run, which is why the
   * photograph is stored beside the name rather than looked up later. Keep the
   * two readers in step.
   */
  function readStoredWatch() {
    var raw;
    try { raw = localStorage.getItem(STORAGE_KEY); } catch (e) { return null; }
    if (!raw) return null;

    var record = null;
    try { record = JSON.parse(raw); } catch (e) { record = null; }
    if (record && typeof record === 'object' && record.name) return record;
    /*
      The first version stored the bare name, and a mission can be a bare year
      such as "2024", which parses as JSON and is not a record.
    */
    return { name: raw, thumb: '' };
  }

  /** ?watch= wins, then what this visitor last chose, then the configured default. */
  Bundle.prototype.initialWatch = function () {
    var missions = this.catalogue.missionList();
    var wanted = new URLSearchParams(window.location.search).get('watch');
    if (!wanted) {
      var stored = readStoredWatch();
      if (stored) wanted = stored.name;
    }
    /*
      No watch until one is asked for, where the page is built that way.

      An empty key is not a missing watch, it is the generic listing: rowFor
      falls back to listings[''], which is the strap photographed on its own
      rather than on somebody's MoonSwatch. So the page opens showing every
      strap, and choosing a watch re-photographs them.
    */
    if (!wanted && this.data.startWithoutWatch) {
      return { key: '', name: '', colourways: 0, thumb: '', chosen: false };
    }
    if (!wanted) wanted = this.data.defaultWatch;

    var key = missionKey(wanted || '');
    var hit = missions.filter(function (m) { return m.key === key; })[0];
    if (hit) return hit;
    // Fall back to the best photographed mission rather than an empty grid.
    return missions[0] || { key: '', name: wanted || 'MoonSwatch', colourways: 0, thumb: '' };
  };

  Bundle.prototype.setWatch = function (mission) {
    this.watch = mission;
    /*
      Drop the material filter. It survived a watch change, so filtering to
      suede and then switching to a watch with no suede listings left the chip
      gone from the row and the filter still applied: an empty grid, the
      "no straps" message, and nothing pressed to explain it.

      'all' is the sentinel visible() checks, not '' and not null. Resetting it
      to an empty string is the same empty grid by a different route, since then
      nothing matches instead of everything.
    */
    this.material = 'all';
    this.renderWatch();
    this.renderMaterials();
    this.rebuild();
    this.closeWatchSheet();
    this.writeWatchToUrl();
  };

  /**
   * Put the watch in the URL so the page can be sent to someone.
   *
   * replaceState rather than pushState: switching watch is changing what you
   * are looking at, not navigating, and a dozen switches should not mean a
   * dozen presses of the back button to leave the page. initialWatch already
   * reads this param and prefers it over the remembered watch.
   */
  Bundle.prototype.writeWatchToUrl = function () {
    if (!window.history || !window.history.replaceState) return;
    if (!this.watch || !this.watch.name) return;
    try {
      var url = new URL(window.location.href);
      url.searchParams.set('watch', missionSlug(this.watch.name));
      window.history.replaceState(window.history.state, '', url.toString());
    } catch (e) { /* a URL the browser will not parse is not worth failing over */ }
  };

  /**
   * One, two or three straps to a row, remembered for next time.
   *
   * Nothing re-renders: the column count is CSS keyed off the root, so the
   * cards already in the DOM simply reflow. That also means the choice survives
   * a filter or a watch change without being reapplied.
   */
  Bundle.prototype.setDensity = function (value) {
    var density = String(value);
    if (density !== '1' && density !== '2' && density !== '3') return;

    this.root.setAttribute('data-density', density);
    this.root.querySelectorAll('[data-bundle-density]').forEach(function (button) {
      button.setAttribute('aria-pressed', button.getAttribute('data-bundle-density') === density ? 'true' : 'false');
    });
    try {
      localStorage.setItem('wb-bundle-density', density);
    } catch (e) { /* private mode, the choice just does not persist */ }
  };

  Bundle.prototype.renderWatch = function () {
    var labels = this.data.labels || {};
    var chosen = !!(this.watch && this.watch.name);

    if (this.root) this.root.setAttribute('data-watch-chosen', chosen ? 'true' : 'false');

    // Nothing picked yet: the control asks the question instead of answering it.
    if (!chosen) {
      this.el.watchName.textContent = labels.pickWatch || 'Which watch do you have?';
      if (this.el.title) this.el.title.textContent = labels.headingAny || 'Build your strap bundle';
      if (this.el.fit) this.el.fit.textContent = labels.pickWatchHint || '';
      if (this.el.watchThumb) this.el.watchThumb.removeAttribute('src');
      return;
    }

    this.el.watchName.textContent = this.watch.name;

    /*
      The heading names what is for sale, not only which watch. The list design
      states its own title in Liquid and does not mark it, so there is nothing
      here to rewrite.
    */
    if (this.el.title) {
      this.el.title.textContent = (labels.headingFor || 'Straps for your %%WATCH%%')
        .replace('%%WATCH%%', this.watch.name);
    }

    // The list design drops the lug line; the grid design still shows it.
    if (this.el.fit) {
      this.el.fit.textContent = (labels.fits || '%%LUG%% mm lug width')
        .replace('%%WATCH%%', this.watch.name)
        .replace('%%LUG%%', this.data.lugWidth);
    }
    if (this.watch.thumb) this.el.watchThumb.src = this.watch.thumb;
    this.rememberWatch();
  };

  /**
   * The watch and its photograph, for the section's inline script to paint on
   * the next load.
   *
   * Written from renderWatch rather than only from setWatch, because the
   * thumbnail is not known until the catalogue has resolved the mission, and
   * because a visitor who never changes watch should still get a painted watch
   * bar on their second visit.
   */
  Bundle.prototype.rememberWatch = function () {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        name: this.watch.name,
        thumb: this.watch.thumb || '',
      }));
    } catch (e) { /* private window */ }
  };

  /* ---------------------------------------------------------------- the visible set */

  /** Every strap that has a listing for this watch, with the row to show. */
  Bundle.prototype.visible = function () {
    var self = this;
    var out = [];
    Object.keys(this.catalogue.straps).forEach(function (key) {
      var strap = self.catalogue.straps[key];
      var row = self.catalogue.rowFor(strap, self.watch.key);
      if (!row) return;
      // Sold out is not a choice, so it is not shown.
      if (!row.a) return;
      var material = strap.material;
      if (self.material !== 'all' && (material.group || 'rubber') !== self.material) return;
      /*
        The typed query, matched against what the row actually says: the
        colourway the list prints and the listing's own title, so "denim" finds
        a Dark Blue Denim and "vertech" finds the range it belongs to.
      */
      if (self.query) {
        var hay = ((row.c || '') + ' ' + (row.t || '') + ' ' + (material.label || '')).toLowerCase();
        if (hay.indexOf(self.query) === -1) return;
      }
      out.push({ strap: strap, row: row, material: material, own: !!strap.listings[self.watch.key] });
    });
    // This watch's own listings first: those photographs show the actual watch.
    return out.sort(function (a, b) {
      if (a.own !== b.own) return a.own ? -1 : 1;
      return a.row.c.localeCompare(b.row.c);
    });
  };

  /**
   * The viewer's item for one row, ignoring what the picker is filtered to.
   *
   * "See it on the watch" used to look the row up in visible(), which is the
   * filtered, in-stock, this-watch set. So the link did nothing whenever the
   * strap it belonged to was not currently in that set: pick a leather strap,
   * tap the Rubber chip, and the link on the slot you already filled was dead.
   * A strap that is in the bundle can always be looked at.
   */
  /**
   * What the link into the photographs says.
   *
   * Only a listing cut for one mission is photographed on the watch. A generic
   * strap is shown on its own, so promising the watch sent the shopper to
   * pictures that do not have it in them.
   */
  Bundle.prototype.seeLabel = function (row) {
    var labels = this.data.labels || {};
    if (row && missionOf(row.t)) return labels.seeOnWatch || 'See it on the watch';
    return labels.seePhotos || 'See photos';
  };

  Bundle.prototype.itemForRow = function (row) {
    if (!row) return null;
    var self = this;
    var found = null;
    Object.keys(this.catalogue.straps).some(function (key) {
      var strap = self.catalogue.straps[key];
      var listings = strap.listings || {};
      var match = Object.keys(listings).filter(function (k) {
        return listings[k] && listings[k].v === row.v;
      })[0];
      if (match === undefined) return false;
      match = listings[match];
      found = {
        strap: strap,
        row: self.catalogue.rowFor(strap, self.watch.key) || match,
        material: strap.material,
        own: !!strap.listings[self.watch.key],
      };
      return true;
    });
    // A row with no strap behind it still has everything the viewer reads.
    if (!found) found = { strap: null, row: row, material: { label: '' }, own: false };
    return found;
  };

  Bundle.prototype.renderMaterials = function () {
    var self = this;
    var counts = {};
    Object.keys(this.catalogue.straps).forEach(function (key) {
      var strap = self.catalogue.straps[key];
      var row = self.catalogue.rowFor(strap, self.watch.key);
      if (!row || !row.a) return;
      var g = strap.material.group || 'rubber';
      if (!counts[g]) counts[g] = { key: g, label: g, n: 0 };
      counts[g].n += 1;
    });

    // Two chips, in a fixed order, and only when they hold something.
    var list = MATERIAL_GROUPS
      .filter(function (g) { return counts[g.key]; })
      .map(function (g) { return { key: g.key, label: g.label, n: counts[g.key].n }; });
    var total = list.reduce(function (sum, m) { return sum + m.n; }, 0);

    this.el.materials.textContent = '';
    var all = this.chip('all', 'All', total);
    this.el.materials.appendChild(all);
    list.forEach(function (m) {
      self.el.materials.appendChild(self.chip(m.key, m.label, m.n));
    });
  };

  /**
   * Searching the picker.
   *
   * The field takes the chip row over rather than sitting under it: another row
   * costs 46px in a box that is already long, and the two are alternatives
   * anyway, since nobody narrows to leather and then types "leather".
   *
   * Typing re-renders the grid alone. rebuild() would replace the rungs too,
   * and the picker lives inside whichever rung is open, so every keystroke
   * would tear the list out from under the person typing into it.
   */
  Bundle.prototype.bindSearch = function () {
    var self = this;
    var filters = this.root.querySelector('[data-bundle-filters]');
    var field = this.root.querySelector('[data-bundle-search]');
    var input = this.root.querySelector('[data-bundle-search-input]');
    var open = this.root.querySelector('[data-bundle-search-open]');
    var close = this.root.querySelector('[data-bundle-search-close]');
    if (!filters || !field || !input || !open || !close) return;

    var show = function (on) {
      filters.setAttribute('data-searching', on ? 'true' : 'false');
      field.hidden = !on;
      open.hidden = on;
      if (on) {
        input.focus();
      } else {
        input.value = '';
        self.query = '';
        self.renderGrid();
      }
    };

    open.addEventListener('click', function () { show(true); });
    close.addEventListener('click', function () { show(false); });

    input.addEventListener('input', function () {
      self.query = input.value.trim().toLowerCase();
      self.renderGrid();
    });

    input.addEventListener('keydown', function (event) {
      // Escape closes rather than only clearing, so one key gets the chips back.
      if (event.key === 'Escape') {
        event.stopPropagation();
        show(false);
      }
    });
  };

  Bundle.prototype.chip = function (key, label, count) {
    var self = this;
    var button = document.createElement('button');
    button.className = 'bundle__material';
    button.type = 'button';
    button.setAttribute('data-bundle-material', key);
    button.setAttribute('aria-pressed', this.material === key ? 'true' : 'false');
    button.innerHTML = label + ' <span class="bundle__material-count">' + count + '</span>';
    button.addEventListener('click', function () {
      self.material = key;
      self.renderMaterials();
      self.rebuild();
    });
    return button;
  };

  /* ---------------------------------------------------------------- pricing */

  Bundle.prototype.percentFor = function (count) {
    var best = 0;
    this.data.tiers.forEach(function (t) { if (count >= t.min && t.percent > best) best = t.percent; });
    return best;
  };

  Bundle.prototype.pricing = function () {
    var count = this.straps.length;
    var percent = this.percentFor(count);
    var strapsFull = this.straps.reduce(function (s, x) { return s + x.price; }, 0);
    var discount = Math.round(strapsFull * percent / 100);

    var groups = {};
    this.straps.forEach(function (strap) {
      strap.closure.forEach(function (c) {
        if (!groups[c.variantId]) {
          groups[c.variantId] = { variantId: c.variantId, label: c.label, price: c.price, isFree: c.isFree, qty: 0 };
        }
        groups[c.variantId].qty += 1;
      });
    });
    var lines = Object.keys(groups).map(function (k) { return groups[k]; });
    var closuresPaid = lines.reduce(function (s, l) { return s + (l.isFree ? 0 : l.price * l.qty); }, 0);

    return {
      count: count, percent: percent, strapsFull: strapsFull, discount: discount,
      closureLines: lines, closuresPaid: closuresPaid,
      total: strapsFull - discount + closuresPaid,
      wasTotal: strapsFull + closuresPaid,
    };
  };

  /* ---------------------------------------------------------------- render */

  /**
   * Everything the bundle state touches, without rebuilding the grid.
   *
   * The grid used to be rebuilt on every add and remove, which replayed the
   * cards' entry animation each time the sheet closed and made the whole grid
   * appear to reload. Prices, badges and steppers are updated in place instead.
   */
  Bundle.prototype.render = function () {
    this.clearSubmitError();
    this.rememberStraps();
    this.refreshGrid();
    this.renderThumbs();
    this.renderList();
    this.renderTiers();
    this.renderTotal();
  };

  /** A different watch or material means different cards, so rebuild those. */
  Bundle.prototype.rebuild = function () {
    this.renderGrid();
    this.renderThumbs();
    this.renderList();
    this.renderTiers();
    this.renderTotal();
  };

  /** Prices, badges and steppers on the cards already on screen. */
  Bundle.prototype.refreshGrid = function () {
    var self = this;
    var pricing = this.pricing();

    /*
      Crossing a tier changes the price on every tile at once, and it used to
      happen silently: the numbers were simply different on the next paint. The
      flip is fired here, on the percentage changing, rather than on every
      render, so ordinary adds within a tier stay still.
    */
    var previousPercent = this.gridPercent;
    var tierMoved = typeof previousPercent === 'number' && previousPercent !== pricing.percent;
    this.gridPercent = pricing.percent;

    this.el.grid.querySelectorAll('.bundle-card').forEach(function (card) {
      var id = Number(card.getAttribute('data-bundle-variant'));
      var row = (self.catalogue.allRows || []).filter(function (r) { return r.v === id; })[0];
      if (!row) return;

      var inBundle = self.straps.filter(function (x) { return x.rowKey === id; }).length;
      /*
        The tier the bundle is actually on, never the one this tile would create.

        Tiles used to price forward, so with one strap in the bundle the
        shopper's own pick read 649,00 kr while every tile beside it read
        551,65 kr with a -15% badge. Twelve of sixteen reviewers called that a
        bug or a trick. The ladder says what more straps are worth; the grid
        says what things cost right now.
      */
      var percent = pricing.percent;
      var now = row.p - Math.round(row.p * percent / 100);

      var nowEl = card.querySelector('.bundle-card__now');
      if (nowEl) {
        nowEl.textContent = self.money(now);
        nowEl.setAttribute('data-discounted', percent > 0 ? 'true' : 'false');
      }
      if (tierMoved) self.flipPrice(card, nowEl);
      self.syncWas(card, row, percent);

      var badge = card.querySelector('.bundle-card__badge');
      if (percent > 0 && !badge) {
        badge = document.createElement('span');
        badge.className = 'bundle-card__badge';
        card.querySelector('.bundle-card__frame').appendChild(badge);
      }
      if (badge) {
        badge.hidden = percent === 0;
        badge.textContent = '\u2212' + percent + '%';
        if (tierMoved) self.flipPrice(card, badge);
      }

      // The add control becomes a stepper once the strap is in, and back again.
      var frame = card.querySelector('.bundle-card__frame');
      var control = frame.querySelector('.bundle-card__add, .bundle-stepper');
      var wantStepper = inBundle > 0;
      var haveStepper = control && control.classList.contains('bundle-stepper');
      if (control && wantStepper !== haveStepper) {
        var next = self.stepper(row, inBundle);
        if (next) control.replaceWith(next);
        else control.remove();
      } else if (haveStepper) {
        control.querySelector('.bundle-stepper__value').textContent = String(inBundle);
      }
    });
  };

  /**
   * Turn one number over, if anyone can see it.
   *
   * The grid can hold two hundred tiles and the discount applies to all of
   * them, so animating the lot would spend a lot of work on cards nobody is
   * looking at. Offscreen tiles simply carry the new number, which is what they
   * would have done anyway.
   *
   * The value is already updated when this runs, so it is a flip in rather than
   * a flip through: the number turns into place, which reads as "this changed"
   * without needing two faces to swap between.
   */
  Bundle.prototype.flipPrice = function (card, el) {
    if (!el || el.hidden) return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var box = card.getBoundingClientRect();
    if (box.bottom < -80 || box.top > window.innerHeight + 80) return;

    // Down the page rather than all at once, so it reads as sweeping the grid.
    var delay = Math.min(Math.max(box.top, 0) / window.innerHeight, 1) * 120;

    el.classList.remove('bundle-flip');
    // Forces the removal to take before the class goes back on, so a second
    // tier change in quick succession restarts rather than doing nothing.
    void el.offsetWidth;
    el.style.animationDelay = Math.round(delay) + 'ms';
    el.classList.add('bundle-flip');

    var done = function () {
      el.classList.remove('bundle-flip');
      el.style.animationDelay = '';
      el.removeEventListener('animationend', done);
    };
    el.addEventListener('animationend', done);
  };

  /**
   * The struck original beside, or above, the discounted price.
   *
   * Created here rather than assumed: the server renders a tile with only its
   * full price, because at zero straps there is no discount to strike. So the
   * element does not exist until a tier is reached, and refreshGrid used to
   * look for one and quietly find nothing, which is why a discounted tile
   * showed a lower number with nothing to compare it against.
   */
  Bundle.prototype.syncWas = function (card, row, percent) {
    var prices = card.querySelector('.bundle-card__prices');
    if (!prices) return;
    var was = prices.querySelector('.bundle-card__was');

    if (percent <= 0) {
      if (was) was.hidden = true;
      return;
    }
    if (!was) {
      was = document.createElement('span');
      was.className = 'bundle-card__was';
      prices.appendChild(was);
    }
    was.hidden = false;
    was.textContent = this.money(row.p);
  };

  Bundle.prototype.renderGrid = function () {
    var self = this;
    var items = this.visible();
    var pricing = this.pricing();
    var frag = document.createDocumentFragment();
    // Variant id back to the item, so the reconcile below can wire up a tile
    // the server drew without rebuilding the list it came from.
    var itemFor = {};

    items.forEach(function (item, index) {
      var row = item.row;
      var inBundle = self.straps.filter(function (s) { return s.rowKey === row.v; }).length;
      /*
        The tier the bundle is actually on, never the one this tile would create.

        Tiles used to price forward, so with one strap in the bundle the
        shopper's own pick read 649,00 kr while every tile beside it read
        551,65 kr with a -15% badge. Twelve of sixteen reviewers called that a
        bug or a trick. The ladder says what more straps are worth; the grid
        says what things cost right now.
      */
      var percent = pricing.percent;
      var now = row.p - Math.round(row.p * percent / 100);

      itemFor[row.v] = item;

      var li = document.createElement('li');
      li.className = 'bundle-card';
      li.setAttribute('data-bundle-variant', row.v);
      li.setAttribute('data-available', row.a ? 'true' : 'false');

      var frame = document.createElement('div');
      frame.className = 'bundle-card__frame';

      var photo = document.createElement('button');
      photo.className = 'bundle-card__photo';
      photo.type = 'button';
      photo.setAttribute('aria-label', row.c);
      photo.addEventListener('click', function () { self.openViewer(item); });
      if (row.img) {
        var img = document.createElement('img');
        img.src = row.img;
        img.alt = row.c;
        img.width = 400;
        img.height = 514;
        img.loading = index < 4 ? 'eager' : 'lazy';
        photo.appendChild(img);
      }
      frame.appendChild(photo);

      /*
        The zoom control. The photo itself opens the viewer too, but a plain
        image gives no sign it is interactive, and the corner clamps are the
        conventional "see this larger".
      */
      var zoom = document.createElement('button');
      zoom.className = 'bundle-card__zoom';
      zoom.type = 'button';
      zoom.setAttribute('data-bundle-zoom', '');
      zoom.setAttribute('aria-label', 'See ' + row.c + ' larger');
      zoom.innerHTML = '<svg viewBox="0 0 14 14" fill="none" aria-hidden="true">' +
        '<path d="M1 5V1h4M13 9v4H9M13 5V1H9M1 9v4h4" stroke="currentColor" stroke-width="1.5" ' +
        'stroke-linecap="round" stroke-linejoin="round"/></svg>';
      zoom.addEventListener('click', function (event) {
        event.stopPropagation();
        self.openViewer(item);
      });
      frame.appendChild(zoom);

      /*
        The badge states the same tier as the price beneath it.

        It read the live tier while the price read the tier the card would
        create, so with one strap in the bundle every other card was priced 15%
        off with nothing saying why.
      */
      if (percent > 0) {
        var badge = document.createElement('span');
        badge.className = 'bundle-card__badge';
        badge.textContent = '\u2212' + percent + '%';
        frame.appendChild(badge);
      }

      if (row.a) {
        // Null in the list design, where the row itself is the control.
        var control = self.stepper(row, inBundle);
        if (control) frame.appendChild(control);
      }
      li.appendChild(frame);

      var name = document.createElement('p');
      name.className = 'bundle-card__name';
      name.textContent = row.c;
      li.appendChild(name);

      // The same named way into the photographs the seeded rows carry.
      if (self.data.layout === 'list') {
        var see = document.createElement('button');
        see.className = 'bundle-card__see';
        see.type = 'button';
        see.setAttribute('data-bundle-see', '');
        see.textContent = self.seeLabel(row);
        li.appendChild(see);
      }

      if (row.a) {
        var prices = document.createElement('p');
        prices.className = 'bundle-card__prices';
        prices.innerHTML =
          '<span class="bundle-card__now" data-discounted="' + (percent > 0) + '">' + self.money(now) + '</span>' +
          (percent > 0 ? '<span class="bundle-card__was">' + self.money(row.p) + '</span>' : '');
        li.appendChild(prices);
      } else {
        var out = document.createElement('p');
        out.className = 'bundle-card__sold-out';
        out.textContent = 'Sold out';
        li.appendChild(out);
      }

      frag.appendChild(li);
    });

    /*
      Reconcile rather than replace.

      Clearing the grid and appending a fresh set moved every tile on screen, so
      a watch change or the first render after boot scored a 0.52 layout shift
      and the document jumped from 1793px to 11272px. Matching tiles are kept
      where they are, new ones are inserted, and the placeholders and anything
      no longer in the set are dropped.
    */
    /*
      Say so when a filter or a query leaves nothing. The element was in the
      markup and nothing ever unhid it, so narrowing to no matches left a blank
      space under the search field and no word about why.
    */
    if (this.el.empty) {
      var labels = this.data.labels || {};
      this.el.empty.hidden = items.length > 0;
      this.el.empty.textContent = this.query
        ? (labels.noSearchMatch || 'No strap matches that.')
        : (labels.noStraps || this.el.empty.textContent);
    }

    var grid = this.el.grid;
    var existing = {};
    Array.prototype.forEach.call(grid.children, function (node) {
      var key = node.getAttribute('data-bundle-variant');
      if (key) existing[key] = node;
    });

    var wanted = Array.prototype.slice.call(frag.children);

    /*
      On the first render, tiles the server already placed keep its order.

      The server picks this watch's own listings first, which is the same rule
      the sort below uses, but it cannot see past the first 50 products so its
      order can differ in detail. Honouring it means the seeded tiles are
      swapped where they stand instead of sliding into a new order.
    */
    if (!this._reconciled) {
      this._reconciled = true;
      var seededOrder = {};
      Array.prototype.forEach.call(grid.children, function (node, index) {
        var key = node.getAttribute('data-bundle-variant');
        if (key) seededOrder[key] = index;
      });
      wanted.sort(function (a, b) {
        var ai = seededOrder[a.getAttribute('data-bundle-variant')];
        var bi = seededOrder[b.getAttribute('data-bundle-variant')];
        if (ai === undefined && bi === undefined) return 0;
        if (ai === undefined) return 1;
        if (bi === undefined) return -1;
        return ai - bi;
      });
    }

    var keep = {};
    wanted.forEach(function (node, index) {
      var key = node.getAttribute('data-bundle-variant');
      keep[key] = true;
      var current = grid.children[index];
      var already = existing[key];

      /*
        A seeded tile came from the server and has no listeners on its controls.
        It used to be swapped for the built one, which cost no layout shift but
        did remount the tile, so bundle-card-in replayed on the first eight
        tiles about three seconds after they had already played it. Measured on
        the preview: the first four cards were at opacity 1 from 1.5s, and back
        at 0 the instant the grid was reconciled.

        So the node the shopper is already looking at is kept, and only what the
        server could not draw is added to it.
      */
      if (already && already.hasAttribute('data-bundle-seeded')) {
        self.adoptSeeded(already, itemFor[key]);
      }

      if (already) {
        // Same tile: leave it in place, or move it if the order changed.
        if (current !== already) grid.insertBefore(already, current || null);
        return;
      }
      grid.insertBefore(node, current || null);
    });

    // Placeholders and tiles for straps no longer shown.
    Array.prototype.slice.call(grid.children).forEach(function (node) {
      var key = node.getAttribute('data-bundle-variant');
      if (!key || !keep[key]) node.remove();
    });

    this.el.empty.hidden = items.length > 0;
  };

  /**
   * Take over a tile the server drew, rather than replacing it.
   *
   * Everything here is something Liquid could not produce: the listeners, the
   * stepper, and the price at the tier the bundle is currently on. The image,
   * the name and the frame are left alone unless they genuinely differ, so the
   * tile is never remounted and never animates a second time.
   */
  Bundle.prototype.adoptSeeded = function (card, item) {
    if (!item) return;
    var self = this;
    var row = item.row;
    var percent = this.pricing().percent;
    var inBundle = this.straps.filter(function (s) { return s.rowKey === row.v; }).length;

    card.removeAttribute('data-bundle-seeded');
    /*
      This tile has already played its entry animation, during the first paint.
      The built grid can want it at a different index, and moving a node
      restarts a CSS animation, so it is taken out of the sequence for good.
    */
    card.classList.add('bundle-card--settled');
    card.setAttribute('data-available', row.a ? 'true' : 'false');

    var photo = card.querySelector('.bundle-card__photo');
    if (photo) photo.addEventListener('click', function () { self.openViewer(item); });

    var zoom = card.querySelector('[data-bundle-zoom]');
    if (zoom) {
      zoom.addEventListener('click', function (event) {
        event.stopPropagation();
        self.openViewer(item);
      });
    }

    /*
      Liquid picked the variant's own image and this picks the row's. They agree
      in practice; where they do not, the row is the one the viewer and the
      sheet will show, so the tile should match it. Only set on a difference,
      because assigning the same src is a needless decode.
    */
    var img = card.querySelector('.bundle-card__photo img');
    // Same photograph, two URL spellings. Liquid's image_url emits
    // foo.jpg?v=1&width=400; sized() emits the legacy foo_400x.jpg?v=1. A plain
    // string compare was therefore always unequal, so every adopted tile
    // re-downloaded a picture it was already showing, which is exactly what
    // adopting it was meant to avoid.
    if (img && row.img && !samePhoto(img.getAttribute('src'), row.img)) img.src = row.img;

    var name = card.querySelector('.bundle-card__name');
    if (name && name.textContent !== row.c) name.textContent = row.c;

    var now = card.querySelector('.bundle-card__now');
    if (now) {
      now.textContent = this.money(row.p - Math.round(row.p * percent / 100));
      now.setAttribute('data-discounted', percent > 0 ? 'true' : 'false');
    }
    this.syncWas(card, row, percent);

    // The plus the server drew has no listener, so this is the one node swapped.
    var add = card.querySelector('.bundle-card__add');
    var built = this.stepper(row, inBundle);
    if (add && built) add.replaceWith(built);
    else if (add) add.remove();
  };

  /**
   * A plus until the strap is in, then a stepper.
   *
   * The same colourway can go in more than once: 26% of two-strap orders are
   * the identical strap twice, so quantity belongs on the card.
   */
  Bundle.prototype.stepper = function (row, quantity) {
    var self = this;
    /*
      The list design has no plus. A row there is a choice made inside a slot,
      so the whole row is the control and a button on the end of it would be a
      second way to do the same thing.
    */
    if (this.data.layout === 'list') return null;
    if (!quantity) {
      var add = document.createElement('button');
      add.className = 'bundle-card__add';
      add.type = 'button';
      add.setAttribute('data-in-bundle', 'false');
      add.setAttribute('aria-label', 'Add ' + row.c);
      add.innerHTML = '<svg viewBox="0 0 15 15" fill="none" aria-hidden="true">' +
        '<path d="M7.5 1v13M1 7.5h13" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>';
      /*
        The sheet is the add. There used to be a second, blind path that forced
        a default closure and never let the shopper near the choice, so the plus
        opened nothing and the free buckle was all anyone could get. Dismissing
        the sheet still keeps the strap, so this costs a shopper who does not
        care one tap on the scrim.
      */
      add.addEventListener('click', function () { self.openSheet(row); });
      return add;
    }

    var wrap = document.createElement('div');
    wrap.className = 'bundle-stepper';

    var minus = document.createElement('button');
    minus.className = 'bundle-stepper__button';
    minus.type = 'button';
    minus.setAttribute('aria-label', 'Remove one ' + row.c);
    minus.innerHTML = '<svg viewBox="0 0 15 15" fill="none" aria-hidden="true">' +
      '<path d="M1 7.5h13" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>';
    minus.addEventListener('click', function () { self.removeRow(row.v); });

    var value = document.createElement('span');
    value.className = 'bundle-stepper__value';
    value.textContent = String(quantity);

    var plus = document.createElement('button');
    plus.className = 'bundle-stepper__button';
    plus.type = 'button';
    plus.setAttribute('aria-label', 'Add another ' + row.c);
    plus.innerHTML = '<svg viewBox="0 0 15 15" fill="none" aria-hidden="true">' +
      '<path d="M7.5 1v13M1 7.5h13" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>';
    // A second copy is its own choice: a shopper buying the same strap twice
    // often wants a different length or closure on the second one.
    plus.addEventListener('click', function () { self.openSheet(row); });

    wrap.appendChild(minus);
    wrap.appendChild(value);
    wrap.appendChild(plus);
    return wrap;
  };

  /**
   * The thumbnails, which are the count. There is deliberately no count line.
   *
   * Four slots, always four: a fifth strap does not widen the row, the last
   * slot counts the remainder instead, so the collapsed panel never grows.
   */
  /** A chevron for a slot that opens. */
  function caret() {
    var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 12 8');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('class', 'bundle-panel__slot-caret');
    var path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', 'M1 1.5 6 6.5l5-5');
    path.setAttribute('stroke', 'currentColor');
    path.setAttribute('stroke-width', '1.5');
    path.setAttribute('stroke-linecap', 'round');
    path.setAttribute('stroke-linejoin', 'round');
    svg.appendChild(path);
    return svg;
  }

  /**
   * Open one slot, or close the one that is open.
   *
   * The picker is a single element that moves into the open slot rather than a
   * list rendered per slot: three copies of sixty seven rows would be three
   * times the DOM and three times the work on every filter, for a list only one
   * of which can ever be visible.
   */
  Bundle.prototype.toggleSlot = function (index) {
    var picker = this.root.querySelector('[data-bundle-picker]');
    if (!picker) return;

    if (this.openSlot === index) {
      this.openSlot = null;
      this.replaceIndex = null;
      this.parkPicker();
      return;
    }

    this.openSlot = index;
    this.parkPicker();

    var slots = this.el.thumbs.querySelectorAll('.bundle-panel__slot');
    var slot = slots[index];
    if (!slot) { this.openSlot = null; return; }

    // Opening a slot that already holds a strap means swapping it, not adding.
    this.replaceIndex = slot.hasAttribute('data-filled') ? index : null;

    this.mountPicker(picker, slot);
  };

  /**
   * Hang the picker off the bottom edge of a slot.
   *
   * A sibling, not a child: the slot is a row with its own grid, and putting
   * the picker inside it meant fighting that box to make it grow. Out of flow,
   * because in flow it grew the box and pushed the total, the tier bar and the
   * checkout button down the page every time a slot was opened.
   */
  Bundle.prototype.mountPicker = function (picker, slot) {
    slot.setAttribute('aria-expanded', 'true');
    slot.setAttribute('data-open', 'true');
    slot.parentNode.insertBefore(picker, slot.nextSibling);
    picker.hidden = false;
    /*
      Measured off the slot, after unhiding so its height is the open one.
      Left and width too, not left: 0 / right: 0: an absolute box is placed
      against its ancestor's padding box, and the slot list is inset, so the
      edges the picker has to line up with are the slot's, not the list's.
    */
    // Minus the rung's own bottom border, so the two edges are one line.
    picker.style.top = (slot.offsetTop + slot.offsetHeight - 1) + 'px';
    picker.style.left = slot.offsetLeft + 'px';
    picker.style.width = slot.offsetWidth + 'px';
  };

  /** Re-attach the picker to the slot that was open, after a rebuild. */
  Bundle.prototype.reopenSlot = function () {
    var picker = this.root.querySelector('[data-bundle-picker]');
    var slots = this.el.thumbs.querySelectorAll('.bundle-panel__slot');
    var slot = slots[this.openSlot];
    if (!picker || !slot) {
      this.openSlot = null;
      return;
    }
    this.mountPicker(picker, slot);
  };

  /** Put the picker back where it lives, and close whatever held it. */
  Bundle.prototype.parkPicker = function () {
    var picker = this.root.querySelector('[data-bundle-picker]');
    var home = this.root.querySelector('[data-bundle-picker-home]');
    if (!picker || !home) return;
    picker.hidden = true;
    picker.style.top = '';
    picker.style.left = '';
    picker.style.width = '';
    if (picker.parentNode !== home) home.appendChild(picker);
    this.root.querySelectorAll('.bundle-panel__slot[data-open]').forEach(function (slot) {
      slot.removeAttribute('data-open');
      slot.setAttribute('aria-expanded', 'false');
    });
  };

  /** The way back into a slot that is already filled. */
  Bundle.prototype.slotChange = function (index) {
    var self = this;
    var button = document.createElement('button');
    button.className = 'bundle-panel__slot-change';
    button.type = 'button';
    /*
      Clears the slot rather than opening the sheet, which this design does not
      show. Changing your mind is picking a different strap, so the only thing
      left to offer is taking this one back out.
    */
    button.type = 'button';
    button.setAttribute('aria-label', (this.data.labels || {}).removeStrap || 'Remove');
    button.textContent = '\u00d7';
    button.addEventListener('click', function (event) {
      event.stopPropagation();
      self.removeAt(index);
    });
    return button;
  };

  /**
   * The line under "Choose a strap" on an empty rung.
   *
   * Only where there is a saving to name. The first rung had a count of the
   * colourways that fit, which said nothing about why to fill it and read as
   * filler next to two lines that were actually offering something.
   */
  Bundle.prototype.slotHint = function (index) {
    var labels = this.data.labels || {};
    var filled = index + 1;
    if (index === 0) return '';

    var reached = this.percentFor(filled);
    if (!reached) return '';
    var template = filled === 2 ? labels.slotHintPair : labels.slotHintMore;
    if (!template) return '';
    return template.replace('%%PERCENT%%', String(reached));
  };

  /**
   * The length, under a filled rung.
   *
   * Picking a strap in a slot takes its first available length without asking,
   * so the length has to stay visible and changeable where the strap sits.
   * Every length is shown with the sold out ones disabled, as the grid design's
   * sheet does. Lengths share a price across this catalogue, so changing one
   * swaps the variant and nothing else.
   */
  Bundle.prototype.slotSizes = function (strap, index) {
    var self = this;
    var lengths = (strap.row && strap.row.lengths) || [];
    if (lengths.length < 2) return null;
    var labels = this.data.labels || {};

    var wrap = document.createElement('div');
    wrap.className = 'bundle-slot-size';

    var head = document.createElement('div');
    head.className = 'bundle-slot-size__head';
    var label = document.createElement('span');
    label.className = 'bundle-slot-size__label';
    label.textContent = labels.size || 'Size';
    head.appendChild(label);

    if (this.guideIdFor(strap.row)) {
      var guide = document.createElement('button');
      guide.type = 'button';
      guide.className = 'bundle-slot-size__guide';
      guide.setAttribute('aria-haspopup', 'dialog');
      guide.textContent = labels.sizeGuide || 'Size guide';
      guide.addEventListener('click', function (event) {
        event.stopPropagation();
        self.openGuideFor(strap.row);
      });
      head.appendChild(guide);
    }
    wrap.appendChild(head);

    var group = document.createElement('div');
    group.className = 'bundle-slot-size__options';
    group.setAttribute('role', 'group');
    group.setAttribute('aria-label', labels.size || 'Size');
    lengths.forEach(function (entry) {
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'bundle-size';
      button.textContent = entry.value;
      button.disabled = !entry.available;
      button.setAttribute('aria-pressed', entry.variantId === strap.variantId ? 'true' : 'false');
      button.addEventListener('click', function (event) {
        event.stopPropagation();
        if (!entry.available || entry.variantId === strap.variantId) return;
        strap.variantId = entry.variantId;
        strap.size = entry.value;
        self.render();
        // render rebuilt the rungs, so focus goes back to the length just chosen.
        var again = self.el.thumbs.querySelectorAll('.bundle-panel__slot')[index];
        var pressed = again && again.querySelector('.bundle-size[aria-pressed="true"]');
        if (pressed) pressed.focus();
      });
      group.appendChild(button);
    });
    wrap.appendChild(group);
    return wrap;
  };

  Bundle.prototype.renderThumbs = function () {
    /*
      Four in the sticky panel, one per rung in the list design. The container
      says which, so the script fills a slot without knowing which design it is
      rendering into.
    */
    var self0 = this;
    var declared = this.el.thumbs && Number(this.el.thumbs.getAttribute('data-slots'));
    var SLOTS = declared > 0 ? declared : 4;
    var labelled = declared > 0;
    var frag = document.createDocumentFragment();
    /*
      The list design has no overflow tile.

      In the sticky panel the slots are 56px thumbnails in a row, so a fourth
      one collapsing into "+2" keeps the row from running off the edge. Here a
      slot is a full width row and there is always somewhere to put another, so
      the same tile rendered as an empty dashed band with a bare "+3" in the
      corner. Every strap gets its own row instead.
    */
    var overflow = labelled ? 0 : this.straps.length - SLOTS;
    var shown = labelled
      ? this.straps.length
      : (overflow > 0 ? SLOTS - 1 : Math.min(this.straps.length, SLOTS));

    for (var i = 0; i < shown; i += 1) {
      var slot = document.createElement('div');
      slot.className = 'bundle-panel__slot';
      var strap = this.straps[i];
      var markEl = null;
      if (labelled) {
        /*
          The square, with no number on it. An empty rung is numbered because
          the number is the only thing telling one blank from the next; a filled
          one has the strap's own photograph, and a badge over that just covered
          part of what the shopper picked.
        */
        markEl = document.createElement('span');
        markEl.className = 'bundle-panel__slot-mark';
        slot.appendChild(markEl);
      }
      if (strap.image) {
        var img = document.createElement('img');
        img.src = strap.image;
        img.alt = '';
        (markEl || slot).appendChild(img);
      }
      if (labelled) {
        slot.setAttribute('data-filled', 'true');

        var text = document.createElement('span');
        text.className = 'bundle-panel__slot-text';

        var name = document.createElement('span');
        name.className = 'bundle-panel__slot-name';
        name.textContent = strap.title;
        text.appendChild(name);

        // The same named way into the photographs the picker rows carry.
        var see = document.createElement('button');
        see.className = 'bundle-card__see';
        see.type = 'button';
        see.textContent = self0.seeLabel(strap.row);
        (function (row) {
          see.addEventListener('click', function (event) {
            event.stopPropagation();
            var item = self0.itemForRow(row);
            if (item) self0.openViewer(item);
          });
        }(strap.row));
        text.appendChild(see);
        slot.appendChild(text);

        /*
          Priced the way the picker rows are: what it costs now, over what it
          cost. At the tier the bundle is on, so the slots add up to the total
          directly beneath them rather than contradicting it.
        */
        var percent = self0.pricing().percent;
        var unit = strap.price - Math.round(strap.price * percent / 100);
        var prices = document.createElement('span');
        prices.className = 'bundle-panel__slot-prices';
        var now = document.createElement('span');
        now.className = 'bundle-panel__slot-price';
        now.textContent = self0.money(unit);
        prices.appendChild(now);
        if (percent > 0) {
          var was = document.createElement('span');
          was.className = 'bundle-panel__slot-was';
          was.textContent = self0.money(strap.price);
          prices.appendChild(was);
        }
        slot.appendChild(prices);

        slot.appendChild(self0.slotChange(i));

        var sizes = self0.slotSizes(strap, i);
        if (sizes) slot.appendChild(sizes);

        /*
          A filled slot reopens. Changing your mind about strap two should not
          mean removing it and starting again: the picker opens on that slot and
          whatever is chosen replaces what is there.
        */
        slot.setAttribute('role', 'button');
        slot.setAttribute('tabindex', '0');
        (function (index) {
          var swap = function (event) {
            if (event.target.closest('.bundle-card__see, .bundle-panel__slot-change, .bundle-slot-size')) return;
            event.preventDefault();
            self0.toggleSlot(index);
          };
          slot.addEventListener('click', swap);
          slot.addEventListener('keydown', function (event) {
            if (event.key === 'Enter' || event.key === ' ') swap(event);
          });
        }(i));
      }
      frag.appendChild(slot);
    }

    if (overflow > 0) {
      var more = document.createElement('div');
      more.className = 'bundle-panel__slot bundle-panel__slot--more';
      more.textContent = '+' + (overflow + 1);
      frag.appendChild(more);
    } else {
      for (var e = shown; e < SLOTS; e += 1) {
        var empty = document.createElement('div');
        empty.className = 'bundle-panel__slot bundle-panel__slot--empty';
        // The fade implies "keep going" rather than three identical blanks.
        empty.setAttribute('data-step', String(Math.min(e - shown, 2)));
        if (labelled) {
          var labels = self0.data.labels || {};

          /*
            A dashed square standing in for the strap that is not there yet.

            No number on it, and no emphasis on the next one to fill. Three
            rungs that do the same thing looked like three different controls
            when one was outlined and each was labelled with its position.
          */
          if (e === shown) empty.setAttribute('data-active', 'true');
          var mark = document.createElement('span');
          mark.className = 'bundle-panel__slot-mark';
          empty.appendChild(mark);

          var text = document.createElement('span');
          text.className = 'bundle-panel__slot-text';

          var say = document.createElement('span');
          say.className = 'bundle-panel__slot-name';
          say.textContent = labels.chooseAStrap
            || (labels.chooseStrap || 'Choose strap %%N%%').replace('%%N%%', String(e + 1));
          text.appendChild(say);

          /*
            What this slot is worth, in a sentence rather than a badge.

            The saving is the tier filling it would reach, so slot three offers
            the top rung rather than the one the bundle is on, which is the
            reason to fill it. The first slot has no saving to offer, so it says
            how much there is to choose from instead.
          */
          var hint = self0.slotHint(e);
          if (hint) {
            var hintEl = document.createElement('span');
            hintEl.className = 'bundle-panel__slot-hint';
            hintEl.textContent = hint;
            text.appendChild(hintEl);
          }
          empty.appendChild(text);

          empty.appendChild(caret());
          empty.setAttribute('role', 'button');
          empty.setAttribute('tabindex', '0');
          empty.setAttribute('aria-expanded', 'false');
          (function (index) {
            var open = function (event) {
              event.preventDefault();
              self0.toggleSlot(index);
            };
            empty.addEventListener('click', open);
            empty.addEventListener('keydown', function (event) {
              if (event.key === 'Enter' || event.key === ' ') open(event);
            });
          }(e));
        }
        frag.appendChild(empty);
      }
    }

    /*
      Past the top rung the target is whatever is in the bundle, so the counter
      reads 4/4 rather than 4/3, which looks like a bug rather than a bonus.
    */
    var counter = this.root.querySelector('[data-bundle-count]');
    if (counter) counter.textContent = this.straps.length + '/' + Math.max(SLOTS, this.straps.length);

    // The slots are about to be replaced, so the picker goes home first or it
    // would be destroyed along with the slot it is sitting in.
    this.parkPicker();
    this.el.thumbs.textContent = '';
    this.el.thumbs.appendChild(frag);

    /*
      Put the picker back where it was. renderThumbs replaces every slot, so
      anything open is destroyed and rebuilt closed. Choosing a filter goes
      through here, which is why the list shut itself the moment you narrowed
      it, which is the opposite of what narrowing is for.
    */
    if (typeof this.openSlot === 'number') this.reopenSlot();
  };

  /**
   * The money.
   *
   * Collapsed it is one line: the total, with the pre-discount figure struck
   * beside it. Opened it becomes two aligned rows, because three numbers and a
   * label crammed onto a single line read as a jumble rather than a summary.
   */
  Bundle.prototype.renderTotal = function () {
    var p = this.pricing();
    this.el.total.textContent = '';

    if (!p.count) {
      /*
        A zero where the real price will be, greyed, with what to do about it
        beside it. "Nothing added yet" said the same thing in a spot the eye
        had no reason to visit.
      */
      if (this.data.layout === 'list') {
        this.el.total.setAttribute('data-layout', 'headline');
        var zero = document.createElement('span');
        zero.className = 'bundle-price__now';
        zero.setAttribute('data-empty', 'true');
        zero.textContent = this.money(0);
        this.el.total.appendChild(zero);

        var hint = document.createElement('span');
        hint.className = 'bundle-price__hint';
        hint.textContent = (this.data.labels && this.data.labels.emptyPriceHint)
          || (this.data.labels && this.data.labels.nothingYet)
          || '';
        this.el.total.appendChild(hint);
        return;
      }

      var note = document.createElement('span');
      note.className = 'bundle-panel__empty-note';
      note.textContent = (this.data.labels && this.data.labels.nothingYet) || 'Nothing added yet';
      this.el.total.appendChild(note);
      return;
    }

    var row = function (labelText, valueText, kind) {
      var line = document.createElement('div');
      line.className = 'bundle-panel__money';
      if (kind) line.setAttribute('data-kind', kind);
      var label = document.createElement('span');
      label.className = 'bundle-panel__money-label';
      label.textContent = labelText;
      var value = document.createElement('span');
      value.className = 'bundle-panel__money-value';
      value.textContent = valueText;
      line.appendChild(label);
      line.appendChild(value);
      return line;
    };

    /*
      The list design states the price the way the reference does: what it was,
      struck, then what it is, large, then the saving as a pill under them. The
      grid design keeps its itemised rows, which suit a panel that has a strap
      list under it.
    */
    if (this.data.layout === 'list') {
      this.el.total.setAttribute('data-layout', 'headline');

      if (p.discount > 0) {
        var wasEl = document.createElement('span');
        wasEl.className = 'bundle-price__was';
        wasEl.textContent = this.money(p.wasTotal);
        this.el.total.appendChild(wasEl);
      }

      var nowEl = document.createElement('span');
      nowEl.className = 'bundle-price__now';
      nowEl.setAttribute('data-discounted', p.discount > 0 ? 'true' : 'false');
      nowEl.textContent = this.money(p.total);
      this.el.total.appendChild(nowEl);

      if (p.discount > 0) {
        var labels = this.data.labels || {};
        var pill = document.createElement('span');
        pill.className = 'bundle-price__pill';
        pill.textContent = (labels.savePercent || 'Save %%PERCENT%%%')
          .replace('%%PERCENT%%', String(p.percent));
        this.el.total.appendChild(pill);
      }
      return;
    }

    if (this.expanded) {
      this.el.total.setAttribute('data-layout', 'rows');
      var count = p.count + (p.count === 1 ? ' strap' : ' straps');

      if (p.discount > 0) {
        this.el.total.appendChild(row(count, this.money(p.wasTotal), 'was'));
        this.el.total.appendChild(row('You save ' + p.percent + '%', '\u2212' + this.money(p.discount), 'save'));
      } else {
        this.el.total.appendChild(row(count, this.money(p.wasTotal), 'plain'));
      }
      this.el.total.appendChild(row('Total', this.money(p.total), 'total'));
      return;
    }

    // Collapsed: the total, and what it was.
    this.el.total.setAttribute('data-layout', 'inline');
    if (p.discount > 0) {
      var was = document.createElement('span');
      was.className = 'bundle-panel__was';
      was.textContent = this.money(p.wasTotal);
      this.el.total.appendChild(was);
    }
    var now = document.createElement('span');
    now.className = 'bundle-panel__now';
    now.textContent = this.money(p.total);
    this.el.total.appendChild(now);
  };

  /**
   * One line per strap, with the closure it will ship with.
   *
   * A check-and-remove list, not a second cart: no product titles, no quantity
   * steppers, no add-on disclosure. The cart drawer does that job later.
   */
  Bundle.prototype.renderList = function () {
    var self = this;
    // The list design has no line items: its slots are the list.
    if (!this.el.list) return;
    var frag = document.createDocumentFragment();

    this.straps.forEach(function (strap, index) {
      var row = document.createElement('div');
      row.className = 'bundle-strap';

      var photo = document.createElement('img');
      photo.className = 'bundle-strap__photo';
      photo.src = strap.image || '';
      photo.alt = '';
      row.appendChild(photo);

      var text = document.createElement('div');
      text.className = 'bundle-strap__text';

      var name = document.createElement('span');
      name.className = 'bundle-strap__name';
      // The colourway and its material, never the product title.
      name.textContent = strap.title + (strap.material ? ' \u00b7 ' + strap.material.toLowerCase() : '');
      text.appendChild(name);

      /*
        The closure line is the way to change it. + adds the strap with the
        included buckle, so this is where an upgrade or a clasp is chosen,
        after the strap is safely in rather than before.
      */
      var closure = strap.closure && strap.closure[0];
      var line = document.createElement('button');
      line.className = 'bundle-strap__closure';
      line.type = 'button';
      line.setAttribute('aria-label', 'Change the closure on ' + strap.title);
      if (closure && closure.image) {
        var icon = document.createElement('img');
        icon.src = closure.image;
        icon.alt = '';
        line.appendChild(icon);
      }
      var label = document.createElement('span');
      label.textContent = closure ? closure.label : 'Choose a closure';
      line.appendChild(label);
      var caret = document.createElement('span');
      caret.className = 'bundle-strap__edit';
      caret.setAttribute('aria-hidden', 'true');
      caret.textContent = 'Change';
      line.appendChild(caret);
      line.addEventListener('click', function (event) {
        event.stopPropagation();
        if (strap.row) self.openSheet(strap.row, index);
      });
      text.appendChild(line);
      row.appendChild(text);

      /*
        The same price the tile shows.

        The list printed the full price and left the discount as one lump in the
        total, so at the 15% tier a strap read 551,65 kr in the grid and
        649,00 kr in the panel. That contradiction was the most raised
        complaint in the review.
      */
      var price = document.createElement('span');
      price.className = 'bundle-strap__price';
      var linePercent = self.pricing().percent;
      var lineNow = strap.price - Math.round(strap.price * linePercent / 100);
      if (linePercent > 0) {
        var was = document.createElement('span');
        was.className = 'bundle-strap__was';
        was.textContent = self.money(strap.price);
        price.appendChild(was);
      }
      var now = document.createElement('span');
      now.textContent = self.money(lineNow);
      price.appendChild(now);
      row.appendChild(price);

      var remove = document.createElement('button');
      remove.className = 'bundle-strap__remove';
      remove.type = 'button';
      remove.setAttribute('aria-label', 'Remove ' + strap.title);
      remove.innerHTML = '<svg viewBox="0 0 9 9" fill="none" aria-hidden="true">' +
        '<path d="M1 1l7 7M8 1L1 8" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>';
      remove.addEventListener('click', function (event) {
        event.stopPropagation();
        self.removeAt(index);
      });
      row.appendChild(remove);

      frag.appendChild(row);
    });

    this.el.list.textContent = '';
    this.el.list.appendChild(frag);
  };

  Bundle.prototype.renderTiers = function () {
    var count = this.straps.length;
    /*
      The ladder is whatever the page was configured with, not three rungs.
      A merchant adding a fourth tier gets a fourth segment from the section and
      has to get a fourth step here too, or the bar would draw a rung the panel
      never lights.
    */
    var tiers = this.data.tiers || [];
    var active = 0;
    tiers.forEach(function (tier, index) {
      if (count >= tier.min) active = index + 1;
    });
    var top = tiers.length;
    var previous = typeof this.tier === 'number' ? this.tier : 0;
    this.tier = active;

    this.root.querySelectorAll('[data-bundle-seg]').forEach(function (seg) {
      var i = Number(seg.getAttribute('data-bundle-seg'));
      // Reached or not; the segment's own colour comes from its position.
      seg.setAttribute('data-reached', i <= active ? 'true' : 'false');
    });

    /*
      Arm the fill only after the first render, so a bundle restored from a
      previous visit is simply in its state rather than animating in as though
      the shopper had just done it.
    */
    if (this.el.panel && this.el.panel.getAttribute('data-armed') !== 'true') {
      var panel = this.el.panel;
      requestAnimationFrame(function () { panel.setAttribute('data-armed', 'true'); });
    }

    if (top > 0 && active === top && previous < top) this.celebrate();

    this.root.querySelectorAll('[data-bundle-tier]').forEach(function (label) {
      var i = Number(label.getAttribute('data-bundle-tier'));
      label.setAttribute('data-active', i === active ? 'true' : 'false');
    });

    /*
      The button names the saving once there is one, so the reason to go to
      checkout is on the thing being pressed rather than only in the ladder
      above it. At one strap there is no discount, so it stays plain.
    */
    this.setEmpty();

    var labels = this.data.labels || {};
    var saving = this.pricing().discount;
    var text;
    if (count === 0) {
      /*
        The list design names the action and leaves the button disabled, rather
        than replacing it with an instruction: the slots above already say what
        to do, and a button that changes its words as well as its state reads
        as two different buttons.
      */
      text = this.data.layout === 'list'
        ? (labels.addToCart || 'Add to cart')
        : (labels.pickToStart || 'Pick a strap to start');
    } else if (saving > 0 && labels.addSaving) {
      text = labels.addSaving.replace('%%AMOUNT%%', this.money(saving));
    } else if (saving > 0 && labels.checkoutSaving) {
      text = labels.checkoutSaving.replace('%%AMOUNT%%', this.money(saving));
    } else {
      text = labels.addToCart || labels.checkout || 'Checkout';
    }
    this.el.submit.disabled = count === 0;
    this.el.submit.textContent = text;
  };

  /**
   * Mark the last tier, once, on the way in.
   *
   * There is nothing further to earn past it, so it is the one moment in the
   * ladder worth calling out. Driven by an attribute the stylesheet animates
   * off, removed on animationend so a later add can fire it again after the
   * shopper drops back down and returns.
   */
  Bundle.prototype.celebrate = function () {
    var tiers = this.root.querySelector('.bundle-panel__tiers');
    if (!tiers) return;

    // The tier's own line brightens, which is the part that says what happened.
    var done = function () {
      tiers.removeAttribute('data-celebrate');
      tiers.removeEventListener('animationend', done);
    };
    tiers.removeAttribute('data-celebrate');
    // Reading layout between the removal and the set restarts the animation
    // rather than letting the browser collapse the two into no change at all.
    void tiers.offsetWidth;
    tiers.addEventListener('animationend', done);
    tiers.setAttribute('data-celebrate', 'true');

    this.spark(tiers);
  };

  /**
   * The burst, off the segment that just filled.
   *
   * Spawned rather than declared because the origin is wherever the last
   * segment happens to be, which depends on how many rungs the page was
   * configured with. Each spark gets its own angle and distance and removes
   * itself, so nothing accumulates in the panel.
   */
  Bundle.prototype.spark = function (tiers) {
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var segs = tiers.querySelectorAll('[data-bundle-seg]');
    var last = segs[segs.length - 1];
    if (!last) return;

    var box = tiers.getBoundingClientRect();
    var seg = last.getBoundingClientRect();
    var originX = seg.right - box.left - seg.width / 2;
    var originY = seg.top - box.top + seg.height / 2;

    var colours = ['var(--bp-tier-3)', 'var(--bp-tier-2)', 'var(--bp-panel-accent)', 'var(--bp-tier-1)'];
    var count = 12;
    var frag = document.createDocumentFragment();

    for (var i = 0; i < count; i += 1) {
      // Fanned across the full circle, biased upward so the burst reads off the
      // bar rather than down into the button.
      var angle = (Math.PI * 2 * i) / count - Math.PI / 2;
      var spread = 18 + Math.random() * 16;
      var spark = document.createElement('span');
      spark.className = 'bundle-panel__spark';
      spark.style.left = originX + 'px';
      spark.style.top = originY + 'px';
      spark.style.background = colours[i % colours.length];
      spark.style.setProperty('--dx', Math.round(Math.cos(angle) * spread) + 'px');
      spark.style.setProperty('--dy', Math.round(Math.sin(angle) * spread * 0.8) + 'px');
      spark.style.animationDelay = Math.round(Math.random() * 60) + 'ms';
      spark.addEventListener('animationend', function () { this.remove(); });
      frag.appendChild(spark);
    }
    tiers.appendChild(frag);
  };

  /* ---------------------------------------------------------------- opening */

  Bundle.prototype.setEmpty = function () {
    // Drives the shorter panel and the hidden button while nothing is picked.
    this.el.panel.setAttribute('data-empty', this.straps.length ? 'false' : 'true');
  };

  Bundle.prototype.setExpanded = function (open) {
    this.expanded = !!open && this.straps.length > 0;
    this.el.panel.setAttribute('data-expanded', this.expanded ? 'true' : 'false');
    if (this.el.handle) this.el.handle.setAttribute('aria-expanded', this.expanded ? 'true' : 'false');
    this.renderTotal();
  };

  Bundle.prototype.holdOpen = function () {
    window.clearTimeout(this._collapseTimer);
  };

  /* ---------------------------------------------------------------- sheets */


  /**
   * Add a strap with what it ships with: the included closure and the first
   * size offered. Both stay changeable from its row in the panel.
   */
  /**
   * Open the sheet to add a strap, or to edit one already in the bundle.
   *
   * An edit has to arrive carrying the strap's current state. The sheet used to
   * build `pending` empty either way, press the first length and re-check the
   * free closure, so a shopper who reopened a row to check the size and then
   * confirmed lost a paid clasp and their chosen length without doing anything
   * that reads as a change. `editing` is what renderSizes and resetClosure
   * consult instead of always defaulting.
   */
  /**
   * Take a strap on its defaults, with no sheet.
   *
   * The list design asks nothing on the way in: picking a strap in a slot is a
   * choice, and stopping to ask for a length and a closure turns it back into
   * an add. It still goes through confirmPending, so there is one add path and
   * not two, which is the fracture that produced most of this file's bugs.
   *
   * The length is the first available one, never a sold out one: /cart/add.js
   * takes the whole bundle as a batch and answers 422 for all of it if any line
   * is dead.
   */
  Bundle.prototype.selectStrap = function (row) {
    var lengths = (row.lengths || []).filter(function (l) { return l.available; });
    if (!lengths.length) lengths = row.lengths || [];

    // Whatever the sheet last held is not this strap's closure.
    if (this.el.closureHost) this.el.closureHost.innerHTML = '';

    var replacing = typeof this.replaceIndex === 'number' ? this.replaceIndex : null;
    this.pending = {
      row: row,
      size: lengths[0] || null,
      editIndex: replacing,
      editing: replacing === null ? null : this.straps[replacing],
    };
    this.replaceIndex = null;
    this.confirmPending();
  };

  Bundle.prototype.openSheet = function (row, editIndex) {
    var isEdit = typeof editIndex === 'number' && this.straps[editIndex];
    var editing = isEdit ? this.straps[editIndex] : null;

    this.pending = {
      row: row,
      size: null,
      editIndex: isEdit ? editIndex : null,
      editing: editing,
    };
    this.el.sheetName.textContent = row.c;
    /*
      The tier the bundle is on, never the one this strap would create. The grid
      had forward pricing taken out of it deliberately, so the sheet quoting a
      discount the shopper has not earned yet would contradict the tile they
      tapped to get here.
    */
    this.el.sheetPrice.textContent = this.money(this.unitAt(this.straps.length, row.p));
    if (row.img) this.el.sheetThumb.src = row.img;
    this.renderSizes(row);
    this.loadClosure(row);
    this.el.sheet.setAttribute('open', '');
  };

  Bundle.prototype.unitAt = function (count, price) {
    return price - Math.round(price * this.percentFor(count) / 100);
  };

  Bundle.prototype.closeSheet = function () {
    this.el.sheet.removeAttribute('open');
    this.pending = null;
  };

  /**
   * Dismissing the sheet keeps the strap.
   *
   * The sheet exists to upgrade the closure, not to gate the add: the free
   * buckle is already selected when it opens, so closing it means "the free one
   * is fine" rather than "forget the strap". Cancelling an add is the minus on
   * the card or the remove in the panel.
   */
  Bundle.prototype.dismissSheet = function () {
    if (this.pending) {
      this.confirmPending();
      return;
    }
    this.closeSheet();
  };

  /**
   * Lengths for this listing, taken from the rows themselves.
   *
   * Only shown when the listing actually has a length option. Every Moonphase
   * listing does; the mission-specific Mercury rubber does not.
   */
  Bundle.prototype.renderSizes = function (row) {
    var self = this;
    // Every length offered, with the unavailable ones disabled rather than
    // dropped: filtering to available ones left a single choice, which hid the
    // field entirely and made the strap look sizeless.
    var lengths = row.lengths || [];

    this.el.sizeField.hidden = lengths.length < 2;
    this.el.sizes.textContent = '';
    if (lengths.length < 2) return;

    /*
      What starts pressed. Never a sold-out length: index 0 used to be pressed
      and assigned unconditionally, so a colourway whose first length had sold
      out opened with that length chosen, and /cart/add.js answered 422 for the
      whole batch, taking every other strap in the bundle down with it.

      An edit reopens on the length the shopper already picked.
    */
    var editing = this.pending && this.pending.editing;
    var chosen = null;
    if (editing) {
      chosen = lengths.filter(function (l) { return l.variantId === editing.variantId; })[0] || null;
    }
    if (!chosen) chosen = lengths.filter(function (l) { return l.available; })[0] || null;

    lengths.forEach(function (entry) {
      var button = document.createElement('button');
      button.className = 'bundle-size';
      button.type = 'button';
      button.textContent = entry.value;
      button.disabled = !entry.available;
      button.setAttribute('aria-pressed', entry === chosen ? 'true' : 'false');
      button.addEventListener('click', function () {
        self.el.sizes.querySelectorAll('.bundle-size').forEach(function (b) {
          b.setAttribute('aria-pressed', 'false');
        });
        button.setAttribute('aria-pressed', 'true');
        if (self.pending) self.pending.size = entry;
      });
      self.el.sizes.appendChild(button);
    });
    this.pending.size = chosen;
  };

  /**
   * Fetch the closure panel from the strap's own product page.
   *
   * The panel is the PDP add-on panel and it is driven by that product's
   * metafields, so it cannot be rendered from a collection template: there is
   * no product object. Rendering the strap's product section and lifting the
   * panel out of it keeps this the same component rather than a copy, and it
   * gets the closures that product actually offers.
   *
   * Cached per product handle, so switching between colours of one listing
   * fetches once.
   */
  /**
   * Which closure set a strap belongs to.
   *
   * The buckle and clasp lists are product level, but the free buckle can be
   * overridden per variant, so the key has to be the variant. Keying by product
   * handle, as this first did, preselected another colourway's free buckle on a
   * quarter of the grid and put that variant id into the cart.
   */
  /**
   * Whether this strap has closures coming at all.
   *
   * The map carries the buckle and clasp lists per product, so a strap that
   * offers neither is known before the fetch. Without this the placeholder went
   * up for every strap and then collapsed on the ones with nothing to show,
   * which moves the sheet further than having no placeholder at all.
   */
  Bundle.prototype.expectsClosure = function (row) {
    var map = this.closureMap;
    if (!map || !map.products) return false;
    var product = map.products[row.h];
    if (!product) return false;
    var buckles = product.b;
    var clasps = product.c;
    var has = function (list) {
      if (!list) return false;
      return list.length > 0;
    };
    return has(buckles) || has(clasps);
  };

  Bundle.prototype.signatureFor = function (row) {
    var map = this.closureMap;
    if (!map) return null;
    var product = map.products[row.h];
    if (!product) return null;
    var free = map.overrides[row.v] || product.f;
    return product.b + '|' + product.c + '|' + free;
  };

  /**
   * Fetch every distinct closure panel once, in the background.
   *
   * 349 grid rows resolve to a handful of closure sets, so fetching per listing
   * did roughly forty times the work the data contains and still could not be
   * instant: a Shopify render round trip costs a couple of hundred milliseconds
   * at best, and a touch device has no hover to prefetch on. Rendering each
   * distinct panel once and holding it makes opening the sheet a synchronous
   * DOM operation.
   */
  Bundle.prototype.prefetchClosures = function () {
    var self = this;
    if (!this.closureMap) return;

    var representatives = {};
    (this.catalogue.allRows || []).forEach(function (row) {
      var signature = self.signatureFor(row);
      if (!signature || representatives[signature]) return;
      representatives[signature] = row;
    });

    var work = Object.keys(representatives).map(function (signature) {
      return self.fetchClosure(representatives[signature], signature);
    });

    return Promise.all(work.map(function (promise) {
      return promise.catch(function () {});
    }));
  };

  /** Fetch one panel and keep it under its signature. */
  Bundle.prototype.fetchClosure = function (row, signature) {
    var self = this;
    if (!this.closureCache) this.closureCache = {};
    if (!this.guideCache) this.guideCache = {};
    var key = signature || this.signatureFor(row) || ('handle:' + row.h);
    if (this.closureCache[key]) return Promise.resolve(key);

    // The variant matters: the free buckle can be overridden on it.
    var url = '/products/' + encodeURIComponent(row.h) + '?view=closure-fragment&variant=' + row.v;

    return fetch(url, { headers: { Accept: 'text/html' } })
      .then(function (r) {
        if (!r.ok) throw new Error('closure fragment ' + r.status);
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var panel = doc.querySelector('[data-pdp-addons]');
        if (!panel) throw new Error('no closure panel on that product');

        /*
          The panel carries closure, tools and storage as three tabs. This page
          asks for a closure per strap and nothing else, so the other two go and
          the tab strip goes with them: a tablist with one tab in it is a
          heading that looks clickable.
        */
        panel.querySelectorAll('[data-pdp-panel]').forEach(function (node) {
          if (node.getAttribute('data-pdp-panel') !== 'closure') node.remove();
        });
        panel.querySelectorAll('.pdp-addons__tabs').forEach(function (node) { node.remove(); });
        panel.querySelectorAll('.wb-upsell-v2__group').forEach(function (group) {
          if (group.getAttribute('data-wb-group-root') !== 'pdp-closure') group.remove();
        });

        self.closureCache[key] = panel.outerHTML;

        // The same response carries the product's size guide drawer.
        var guide = doc.querySelector('size-guide-drawer');
        self.guideCache[key] = guide ? guide.outerHTML : '';
        var guideId = self.guideIdFor(row);
        if (guideId) {
          if (!self.guideById) self.guideById = {};
          if (self.guideById[guideId] === undefined) self.guideById[guideId] = guide ? guide.outerHTML : '';
        }
        return key;
      });
  };

  /** Kept for the sheet, which asks for a row rather than a signature. */
  Bundle.prototype.warmClosure = function (row) {
    if (!row || typeof row === 'string') return;
    var key = this.signatureFor(row);
    if (key && this.closureCache && this.closureCache[key]) return;
    this.fetchClosure(row).catch(function () {});
  };

  /**
   * Put the closure into the sheet.
   *
   * Synchronous whenever the panel is already held, which after boot is every
   * strap. A signature the prefetch has not seen, a strap added to the
   * catalogue since the page loaded, falls back to fetching and says so.
   */
  /**
   * A labelled CLOSURE heading with nothing under it reads as a broken sheet.
   *
   * Straps with no closure metafields resolve to an empty panel, which is a
   * legitimate outcome rather than a failure, so the field goes away instead of
   * announcing itself and offering nothing.
   */
  Bundle.prototype.showClosureField = function () {
    var host = this.el.closureHost;
    if (!host) return;
    var field = host.closest ? host.closest('.bundle-field') : host.parentElement;
    if (field) field.hidden = !host.children.length;
  };

  Bundle.prototype.loadClosure = function (row) {
    var self = this;
    var host = this.el.closureHost;
    var key = this.signatureFor(row);

    if (key && this.closureCache && this.closureCache[key]) {
      host.innerHTML = this.closureCache[key];
      this.initClosure();
      this.showGuideLink(this.mountGuide(key));
      this.showClosureField();
      return Promise.resolve();
    }

    /*
      Reserve the height rather than collapsing to nothing. The sheet is sized
      by its content, so an empty host opened it short and then grew it under
      the shopper's thumb once the panel landed.
    */
    /*
      No placeholder. It reserved 315px that then collapsed on every strap in
      the moonswatch collection, because the closure map lists buckles for all
      of them while the rendered panel comes back with no tiles at all. Until
      that disagreement is resolved, reserving the space moves the sheet further
      than leaving it alone. The earlier prefetch is what actually closes the
      window this was meant to cover.
    */
    host.innerHTML = '';
    this.showClosureField();

    /*
      A token, the way renderShots already guards its own late arrivals. Before
      the prefetch has warmed a signature, tapping strap A, dismissing, then
      tapping strap B lets A's response land into B's open sheet: the shopper
      picks from A's closures and A's variant id goes onto B's cart line, and
      mountGuide swaps in A's size guide too.
    */
    this._closureToken = (this._closureToken || 0) + 1;
    var token = this._closureToken;

    return this.fetchClosure(row)
      .then(function (resolved) {
        if (token !== self._closureToken) return;
        host.innerHTML = self.closureCache[resolved] || '';
        self.initClosure();
        self.showGuideLink(self.mountGuide(resolved));
        self.showClosureField();
        if (window.slog) {
          window.slog('custom', {
            message: 'bundle closure fetched outside the prefetch',
            extra: { handle: row.h, variant: row.v },
          });
        }
      })
      .catch(function (error) {
        if (token !== self._closureToken) return;
        // A strap with no closure metafields has no closure to choose.
        host.innerHTML = '';
        self.showClosureField();
        if (window.slog) {
          window.slog('custom', {
            message: 'bundle closure panel unavailable',
            extra: { handle: row.h, error: String(error) },
          });
        }
      });
  };

  /**
   * Put a size guide drawer into the page.
   *
   * The theme's own custom element binds its own click delegation for
   * [data-toggle-id="size-guide-drawer"], so once it is in the document the
   * button opens it with no code of ours. It replaces any previous one, since
   * the id has to stay unique. Returns whether there is one, so the button can
   * be hidden for a strap that has none.
   */
  Bundle.prototype.mountGuide = function (key) {
    var html = this.guideCache && this.guideCache[key];
    var existing = document.getElementById('size-guide-drawer');
    this.placedGuide = null;
    if (existing) {
      if (existing.Drawer) existing.Drawer.destroy();
      existing.remove();
    }
    if (!html) return false;

    var holder = document.createElement('div');
    holder.innerHTML = html;
    var drawer = holder.firstElementChild;
    if (!drawer) return false;
    document.body.appendChild(drawer);
    return true;
  };

  /** Which size guide a strap's listing points at, or null for none. */
  Bundle.prototype.guideIdFor = function (row) {
    var map = this.closureMap;
    var product = row && map && map.products[row.h];
    return product && product.g ? String(product.g) : null;
  };

  /**
   * Open the size guide for one strap.
   *
   * Keyed by the guide rather than by closure set. The catalogue has three
   * guides, rubber, mission leather and generic leather, and straps that share
   * a closure set do not always share a guide, so a guide found through the
   * closure cache could be another material's.
   *
   * Opened from here rather than through data-action="toggle-drawer". The
   * drawer is swapped when a different guide is needed, and a toggle button
   * would be answered both by the new drawer and by whatever delegation an old
   * one left on the body, opening and closing it in the same click.
   */
  Bundle.prototype.openGuideFor = function (row) {
    var self = this;
    var id = this.guideIdFor(row);
    if (!id) return;
    this._guideToken = (this._guideToken || 0) + 1;
    var token = this._guideToken;

    this.fetchGuide(row, id)
      .then(function (html) {
        if (token !== self._guideToken || !html) return;
        var drawer = self.placeGuide(id, html);
        // openDrawer waits its own frame before it animates in.
        if (drawer && drawer.Drawer) drawer.Drawer.openDrawer();
      })
      .catch(function (error) {
        if (window.slog) {
          window.slog('custom', {
            message: 'bundle size guide unavailable',
            extra: { handle: row.h, error: String(error) },
          });
        }
      });
  };

  /** One guide's drawer markup, from any strap that points at it. */
  Bundle.prototype.fetchGuide = function (row, id) {
    var self = this;
    if (!this.guideById) this.guideById = {};
    if (this.guideById[id] !== undefined) return Promise.resolve(this.guideById[id]);

    var url = '/products/' + encodeURIComponent(row.h) + '?view=closure-fragment&variant=' + row.v;
    return fetch(url, { headers: { Accept: 'text/html' } })
      .then(function (r) {
        if (!r.ok) throw new Error('size guide ' + r.status);
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var guide = doc.querySelector('size-guide-drawer');
        self.guideById[id] = guide ? guide.outerHTML : '';
        return self.guideById[id];
      });
  };

  /**
   * Put a guide's drawer into the page, reusing it when it is already there.
   *
   * A replaced drawer is destroyed before it is removed. Removing it alone left
   * its click delegation on the body, still answering the close button of the
   * drawer that replaced it.
   */
  Bundle.prototype.placeGuide = function (id, html) {
    var existing = document.getElementById('size-guide-drawer');
    if (existing && this.placedGuide === id) return existing;
    if (existing) {
      if (existing.Drawer) existing.Drawer.destroy();
      existing.remove();
    }

    var holder = document.createElement('div');
    holder.innerHTML = html;
    var drawer = holder.firstElementChild;
    if (!drawer) return null;
    document.body.appendChild(drawer);
    this.placedGuide = id;
    return drawer;
  };

  Bundle.prototype.showGuideLink = function (hasGuide) {
    var link = this.el.sizeGuide;
    if (link) link.hidden = !hasGuide;
  };

  /** Hand the injected panel to its own script, then default it. */
  Bundle.prototype.initClosure = function () {
    if (window.wbPdpAddons && typeof window.wbPdpAddons.start === 'function') {
      window.wbPdpAddons.start();
    }
    this.resetClosure();
  };

  /**
   * Put the closure panel into the state the sheet should open in.
   *
   * Adding starts on the included buckle. Editing starts on whatever the strap
   * already carries, which is the whole point of the row's Change button: this
   * used to re-check the free tile unconditionally, so a shopper who reopened a
   * row to check the size and confirmed shipped with the free buckle and lost a
   * paid clasp they had chosen and been quoted for.
   */
  Bundle.prototype.resetClosure = function () {
    var panel = this.el.closureHost && this.el.closureHost.querySelector('[data-pdp-addons]');
    if (!panel) return;

    var editing = this.pending && this.pending.editing;
    var wanted = editing && editing.closure && editing.closure.length
      ? editing.closure.map(function (c) { return String(c.variantId); })
      : null;

    var check = function (input) {
      if (!input || input.checked) return true;
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    };

    if (wanted) {
      var restored = 0;
      wanted.forEach(function (id) {
        var tile = panel.querySelector('[data-wb-tile][data-wb-variant-id="' + id + '"]');
        var input = tile && tile.querySelector('[data-wb-tile-input]');
        if (input) restored += check(input) ? 1 : 0;
      });
      // A closure that is no longer offered falls back rather than leaving
      // nothing selected, which would confirm the strap with no closure at all.
      if (restored) return;
    }

    check(panel.querySelector('[data-wb-tile][data-wb-free="true"] [data-wb-tile-input]'));
  };

  Bundle.prototype.readClosure = function () {
    var panel = this.el.closureHost && this.el.closureHost.querySelector('[data-pdp-addons]');
    if (!panel || typeof panel.getSelectedAddonSelections !== 'function') return [];
    return panel.getSelectedAddonSelections().map(function (choice) {
      var tile = panel.querySelector('[data-wb-variant-id="' + choice.variantId + '"]');
      var img = tile && tile.querySelector('img');
      var row = tile && tile.closest('[data-wb-role]');
      var label = (tile && tile.getAttribute('data-wb-label')) || 'Closure';
      return {
        variantId: choice.variantId,
        isFree: choice.isFree,
        price: choice.price,
        label: label,
        image: img ? (img.currentSrc || img.src) : '',
        // The sheet's own vocabulary, translated on the way to the cart.
        role: (row && row.getAttribute('data-wb-role')) || 'buckle',
        title: label,
        variantTitle: (tile && tile.getAttribute('data-wb-variant-title')) || '',
        handle: (tile && tile.getAttribute('data-wb-handle')) || '',
      };
    });
  };

  /**
   * Fly the strap's photograph into the thumbnail row.
   *
   * So the add is visible: the shopper sees the strap they picked land in the
   * bundle rather than a number changing somewhere. Runs on a clone in a fixed
   * layer, so it cannot disturb the grid or the panel, and is skipped when the
   * card is off screen or motion is not wanted.
   */
  Bundle.prototype.flyToBundle = function (fromRect, image) {
    // A selection does not fly. The slot it was chosen in already shows it.
    if (this.data.layout === 'list') return;
    if (!fromRect || !image) return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var slots = this.el.thumbs.querySelectorAll('.bundle-panel__slot');
    var target = slots[Math.min(this.straps.length - 1, slots.length - 1)];
    if (!target) return;
    var to = target.getBoundingClientRect();
    if (!to.width) return;

    var flyer = document.createElement('img');
    flyer.src = image;
    flyer.alt = '';
    flyer.className = 'bundle-flyer';
    flyer.style.left = fromRect.left + 'px';
    flyer.style.top = fromRect.top + 'px';
    flyer.style.width = fromRect.width + 'px';
    flyer.style.height = fromRect.height + 'px';
    document.body.appendChild(flyer);

    var dx = (to.left + to.width / 2) - (fromRect.left + fromRect.width / 2);
    var dy = (to.top + to.height / 2) - (fromRect.top + fromRect.height / 2);
    var scale = to.width / fromRect.width;

    var done = function () { flyer.remove(); };

    if (typeof flyer.animate !== 'function') { done(); return; }
    var run = flyer.animate([
      { transform: 'translate(0, 0) scale(1)', opacity: 1 },
      { transform: 'translate(' + dx + 'px, ' + dy + 'px) scale(' + scale + ')', opacity: 0.85 },
    ], { duration: 460, easing: 'cubic-bezier(0.4, 0, 0.2, 1)', fill: 'forwards' });

    run.addEventListener('finish', done);
    run.addEventListener('cancel', done);
    // A tab switched away mid-flight leaves no animation events, so clean up anyway.
    window.setTimeout(done, 900);
  };

  Bundle.prototype.confirmPending = function () {
    if (!this.pending) return;
    var row = this.pending.row;

    // Measured before the sheet closes and the panel re-renders.
    var card = this.el.grid.querySelector('[data-bundle-variant="' + row.v + '"] .bundle-card__photo img');
    var fromRect = null;
    if (card) {
      var box = card.getBoundingClientRect();
      if (box.width && box.bottom > 0 && box.top < window.innerHeight) fromRect = box;
    }
    // A chosen length is a different variant of the same listing.
    var variantId = this.pending.size ? this.pending.size.variantId : row.v;

    // The list names the colourway and its material, never the product title.
    var item = this.visible().filter(function (x) { return x.row.h === row.h && x.row.c === row.c; })[0];
    var editing = this.pending.editIndex !== null && this.pending.editIndex !== undefined;

    var entry = {
      /*
        Two ids, deliberately. rowKey is the tile this came from and is what the
        grid counts and the minus removes; variantId is the length the shopper
        actually buys. They are the same only for a listing with one length, and
        conflating them meant picking any other length left the card showing a
        plus, addable again and with no minus at all.
      */
      rowKey: row.v,
      variantId: variantId,
      title: row.c,
      price: row.p,
      image: row.img,
      material: item ? item.material.label : '',
      watch: this.watch.name,
      size: this.pending.size ? this.pending.size.value : null,
      closure: this.readClosure(),
      row: row,
    };

    // Editing changes the strap in place; adding appends.
    if (editing) this.straps[this.pending.editIndex] = entry;
    else this.straps.push(entry);

    this.closeSheet();
    this.render();
    // Only a new strap flies into the tray; an edit is already in there.
    if (!editing) this.flyToBundle(fromRect, row.img);
    // The slot it was picked from has done its job.
    if (typeof this.openSlot === 'number') {
      this.openSlot = null;
      this.parkPicker();
    }
  };

  /**
   * Keep the bundle across a reload.
   *
   * Only the identity of each strap is stored, never the row: rows come from
   * products.json and can be stale by the time anyone comes back, so they are
   * looked up again on restore and anything that no longer resolves is dropped.
   * A sold out strap silently returning to the panel would 422 the whole cart
   * at checkout, which is the failure this has to avoid rather than cause.
   */
  Bundle.prototype.rememberStraps = function () {
    try {
      if (!this.straps.length) {
        localStorage.removeItem(BUNDLE_KEY);
        return;
      }
      localStorage.setItem(BUNDLE_KEY, JSON.stringify({
        at: Date.now(),
        watch: this.watch ? this.watch.name : '',
        straps: this.straps.map(function (strap) {
          return {
            rowKey: strap.rowKey,
            variantId: strap.variantId,
            size: strap.size,
            closure: strap.closure || [],
          };
        }),
      }));
    } catch (e) { /* private mode, the bundle just does not survive */ }
  };

  Bundle.prototype.restoreStraps = function () {
    var raw;
    try { raw = localStorage.getItem(BUNDLE_KEY); } catch (e) { return; }
    if (!raw) return;

    var record = null;
    try { record = JSON.parse(raw); } catch (e) { record = null; }
    if (!record || !record.straps || !record.straps.length) return;

    if (!record.at || Date.now() - record.at > BUNDLE_TTL_MS) {
      try { localStorage.removeItem(BUNDLE_KEY); } catch (e) {}
      return;
    }

    var self = this;
    var rows = (this.catalogue && this.catalogue.allRows) || [];
    var restored = [];

    record.straps.forEach(function (saved) {
      var row = rows.filter(function (r) { return r.v === saved.rowKey; })[0];
      // Gone from the catalogue, or the whole colourway has sold out.
      if (!row || !row.a) return;

      // The exact length has to still exist and still be buyable.
      var variantId = saved.variantId;
      if (variantId !== row.v) {
        var length = (row.lengths || []).filter(function (l) {
          return l.variantId === variantId && l.available;
        })[0];
        if (!length) return;
      }

      var item = self.visible().filter(function (x) {
        return x.row.h === row.h && x.row.c === row.c;
      })[0];

      restored.push({
        rowKey: row.v,
        variantId: variantId,
        title: row.c,
        price: row.p,
        image: row.img,
        material: item ? item.material.label : '',
        watch: self.watch ? self.watch.name : '',
        size: saved.size || null,
        closure: saved.closure || [],
        row: row,
      });
    });

    if (!restored.length) return;
    this.straps = restored;

    /*
      Pre-set the tier to where the restore lands. renderTiers celebrates on
      crossing into the top rung, and a bundle coming back from storage has not
      crossed anything: without this a restored three strap bundle would burst
      on page load as though the shopper had just earned it.
    */
    var landing = 0;
    (this.data.tiers || []).forEach(function (tier, index) {
      if (restored.length >= tier.min) landing = index + 1;
    });
    this.tier = landing;

    this.render();
  };

  Bundle.prototype.removeAt = function (index) {
    this.straps.splice(index, 1);
    this.collapseIfEmpty();
    this.render();
  };

  /**
   * An expanded panel over an empty bundle holds its full height to show one
   * line of "Nothing added yet", so removing the last strap closes it.
   */
  Bundle.prototype.collapseIfEmpty = function () {
    if (this.straps.length || !this.expanded) return;
    this.expanded = false;
    if (this.el.panel) this.el.panel.setAttribute('data-expanded', 'false');
  };

  /** By tile, not by variant: the minus lives on a card, not on a length. */
  Bundle.prototype.removeRow = function (rowKey) {
    for (var i = this.straps.length - 1; i >= 0; i -= 1) {
      if (this.straps[i].rowKey === rowKey) {
        this.straps.splice(i, 1);
        break;
      }
    }
    this.collapseIfEmpty();
    this.render();
  };

  /* ---------------------------------------------------------------- photo viewer */

  /**
   * Open one colourway full screen.
   *
   * Built for three shots per colourway, which is what the design asks for, but
   * only one exists: of a listing's images exactly one is bound to each variant
   * and the rest are unbound shots shared across every colour. Showing an
   * unbound one would put a black strap's detail photo under a turquoise strap,
   * so slots two and three are labelled placeholders until the photography
   * exists.
   */
  Bundle.prototype.openViewer = function (item) {
    var self = this;
    var row = item.row;
    this.viewing = item;
    this.viewingShot = 0;

    this.el.viewerName.textContent = row.c;
    this.el.viewerOn.textContent = 'on your ' + this.watch.name;
    this.el.viewerFootName.textContent = row.c;

    // The strike explains itself: the price is qualified by the bundle it joins.
    var inBundle = this.straps.filter(function (x) { return x.rowKey === row.v; }).length;
    /*
      The bundle's real size, not one more. This was the last surface still
      pricing forward: with one strap in, opening the viewer on a second showed
      -15% and "in a bundle of 2" while the tile behind it and the sheet both
      showed the full price.
    */
    var count = this.straps.length;
    var percent = this.percentFor(count);
    var now = row.p - Math.round(row.p * percent / 100);

    var meta = item.material.label;
    if (count > 1) meta += ' \u00b7 in a bundle of ' + count;
    this.el.viewerMeta.textContent = meta;
    this.el.viewerNow.textContent = this.money(now);
    this.el.viewerNow.setAttribute('data-discounted', percent > 0 ? 'true' : 'false');
    this.el.viewerWas.hidden = percent === 0;
    this.el.viewerWas.textContent = this.money(row.p);

    // Real photographs only. Padding the list to three with labelled empty
    // slots advertised pictures that do not exist.
    this.shots = (row.shots && row.shots.length ? row.shots : [row.src]).filter(Boolean);
    this.renderShots();
    this.preloadShots();

    /*
      Nothing to add when it is already in. Opening the viewer from a filled
      slot is looking at a strap you have chosen, so the only thing Add to
      bundle could do there is put a second copy of it in.
    */
    this.el.viewerAdd.hidden = inBundle > 0;

    this.el.viewer.setAttribute('open', '');
    // Warm the closure while the shopper is looking, so Add to bundle is instant.
    this.warmClosure(row);
  };

  /**
   * Fetch every shot for this colourway up front.
   *
   * The swipe itself was instant; the image was not. Moving to a shot whose
   * rendition had never been requested left the old photograph on screen until
   * the new one arrived, seconds later on a phone connection. A carousel
   * library would not have helped, since the wait was the download.
   *
   * The small rendition of every shot first, because that is what a swipe
   * paints immediately, then the large one of the neighbours the shopper is
   * most likely to reach next.
   */
  Bundle.prototype.preloadShots = function () {
    var shots = this.shots || [];
    if (shots.length < 2) return;

    if (!this._preloaded) this._preloaded = {};
    var self = this;

    var warm = function (src, width) {
      var url = sized(src, width);
      if (self._preloaded[url]) return;
      self._preloaded[url] = true;
      var img = new Image();
      img.decoding = 'async';
      img.src = url;
    };

    shots.forEach(function (src) { warm(src, 400); });

    var neighbours = [this.viewingShot + 1, this.viewingShot - 1];
    neighbours.forEach(function (i) {
      var at = ((i % shots.length) + shots.length) % shots.length;
      warm(shots[at], 1400);
    });
  };

  Bundle.prototype.renderShots = function () {
    var self = this;
    var many = this.shots.length > 1;
    var src = this.shots[this.viewingShot] || this.shots[0];

    this.setZoom(false);
    this.el.viewerPhoto.alt = this.viewing ? this.viewing.row.c : '';

    /*
      The small rendition first, then the large one.

      Assigning the large src alone left the previous strap's photograph on
      screen for as long as the new one took to arrive. The 400px rendition is
      the one the grid card already painted, so it comes from cache and there is
      never a stale frame or a blank one.
    */
    var token = (this._shotToken || 0) + 1;
    this._shotToken = token;

    if (!src) {
      this.el.viewerPhoto.removeAttribute('src');
      return;
    }

    this.el.viewerPhoto.src = sized(src, 400);
    this.el.viewerPhoto.setAttribute('data-quality', 'low');

    var full = new Image();
    full.onload = function () {
      // Another shot or another strap was chosen while this was loading.
      if (self._shotToken !== token) return;
      self.el.viewerPhoto.src = full.src;
      self.el.viewerPhoto.setAttribute('data-quality', 'full');
    };
    full.src = sized(src, 1400);

    // One photograph needs no dots and no strip to choose between.
    this.el.viewerDots.hidden = !many;
    this.el.viewerStrip.hidden = !many;
    if (!many) return;

    this.el.viewerDots.textContent = '';
    this.shots.forEach(function (entry, index) {
      var dot = document.createElement('span');
      dot.className = 'bundle-viewer__dot';
      dot.setAttribute('data-active', index === self.viewingShot ? 'true' : 'false');
      self.el.viewerDots.appendChild(dot);
    });

    this.el.viewerStrip.textContent = '';
    this.shots.forEach(function (entry, index) {
      var cell = document.createElement('button');
      cell.className = 'bundle-viewer__thumb';
      cell.type = 'button';
      cell.setAttribute('role', 'tab');
      cell.setAttribute('aria-selected', index === self.viewingShot ? 'true' : 'false');
      cell.setAttribute('aria-label', 'Photograph ' + (index + 1));

      var img = document.createElement('img');
      img.src = sized(entry, 200);
      img.alt = '';
      cell.appendChild(img);

      cell.addEventListener('click', function () {
        self.viewingShot = index;
        self.renderShots();
        self.preloadShots();
      });
      self.el.viewerStrip.appendChild(cell);
    });
  };

  /**
   * Click to zoom the photograph, drag to pan while zoomed.
   *
   * The stage clips, so a zoomed photograph fills it and moves under the
   * pointer rather than escaping the panel.
   */
  Bundle.prototype.setZoom = function (on, originX, originY) {
    var photo = this.el.viewerPhoto;
    this.zoomed = !!on;
    photo.setAttribute('data-zoomed', this.zoomed ? 'true' : 'false');
    if (!this.zoomed) {
      photo.style.transform = '';
      photo.style.transformOrigin = '';
      return;
    }
    photo.style.transformOrigin = (originX == null ? 50 : originX) + '% ' + (originY == null ? 50 : originY) + '%';
    photo.style.transform = 'scale(2.2)';
  };

  Bundle.prototype.bindViewerZoom = function () {
    var self = this;
    var stage = this.el.viewerPhoto.parentElement;

    var pointFor = function (event) {
      var box = self.el.viewerPhoto.getBoundingClientRect();
      return {
        x: Math.min(100, Math.max(0, ((event.clientX - box.left) / box.width) * 100)),
        y: Math.min(100, Math.max(0, ((event.clientY - box.top) / box.height) * 100)),
      };
    };

    this.el.viewerPhoto.addEventListener('click', function (event) {
      // Set by the swipe that just stepped the shot; this click is its tail.
      if (self.swiping) { self.swiping = false; return; }
      var at = pointFor(event);
      self.setZoom(!self.zoomed, at.x, at.y);
    });

    // While zoomed, the pointer moves the photograph under itself.
    stage.addEventListener('pointermove', function (event) {
      if (!self.zoomed) return;
      var at = pointFor(event);
      self.el.viewerPhoto.style.transformOrigin = at.x + '% ' + at.y + '%';
    });

    stage.addEventListener('pointerleave', function () {
      if (self.zoomed) self.setZoom(false);
    });
  };

  /** Move between this colourway's photographs. */
  Bundle.prototype.stepShot = function (delta) {
    if (!this.shots || this.shots.length < 2) return;
    var next = this.viewingShot + delta;
    if (next < 0) next = this.shots.length - 1;
    if (next >= this.shots.length) next = 0;
    this.viewingShot = next;
    this.renderShots();
    this.preloadShots();
  };

  /**
   * Swipe the photograph sideways to change shot.
   *
   * Only when not zoomed, since a drag then pans instead. A mostly vertical
   * drag is left alone so it can scroll or close, and the threshold is 40px so
   * a tap that wanders slightly still reads as a tap.
   */
  Bundle.prototype.bindViewerSwipe = function () {
    var self = this;
    var stage = this.el.viewerPhoto.parentElement;
    var from = null;

    stage.addEventListener('pointerdown', function (event) {
      if (self.zoomed) return;
      from = { x: event.clientX, y: event.clientY };
    });

    stage.addEventListener('pointerup', function (event) {
      if (!from) return;
      var dx = event.clientX - from.x;
      var dy = event.clientY - from.y;
      from = null;
      if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
      /*
        The click that follows this pointerup lands on the photo, whose own
        handler toggles zoom, so a swipe stepped the shot and then zoomed it to
        2.2x at the release point. Swallowing that one click is what separates a
        swipe from a tap.
      */
      self.swiping = true;
      // Dragging left moves forward, the way a photo stack behaves.
      self.stepShot(dx < 0 ? 1 : -1);
    });

    stage.addEventListener('pointercancel', function () { from = null; });

    // Arrow keys do the same, for a keyboard and for the desktop lightbox.
    document.addEventListener('keydown', function (event) {
      if (!self.el.viewer.hasAttribute('open')) return;
      if (event.key === 'ArrowRight') { event.preventDefault(); self.stepShot(1); }
      else if (event.key === 'ArrowLeft') { event.preventDefault(); self.stepShot(-1); }
    });
  };

  Bundle.prototype.closeViewer = function () {
    this.el.viewer.removeAttribute('open');
    this.viewing = null;
  };

  /* ---------------------------------------------------------------- watch picker */

  Bundle.prototype.openWatchSheet = function () {
    this.renderWatchList('');
    this.el.watchSheet.setAttribute('open', '');
    if (this.el.watchSearch) {
      this.el.watchSearch.value = '';
      this.el.watchSearch.focus();
    }
  };

  Bundle.prototype.closeWatchSheet = function () {
    this.el.watchSheet.removeAttribute('open');
  };

  Bundle.prototype.renderWatchList = function (query) {
    var self = this;
    var q = String(query || '').trim().toLowerCase();
    var missions = this.catalogue.missionList().filter(function (m) {
      return !q || m.name.toLowerCase().indexOf(q) !== -1;
    });

    // Annotate only what is known: how many straps are already picked for a
    // watch, and how many colourways it has. No invented purchase history.
    var picked = {};
    this.straps.forEach(function (s) { picked[s.watch] = (picked[s.watch] || 0) + 1; });

    this.el.watchList.textContent = '';
    missions.forEach(function (mission) {
      var li = document.createElement('li');
      var button = document.createElement('button');
      button.className = 'bundle-watch-row';
      button.type = 'button';
      button.setAttribute('aria-current', mission.key === self.watch.key ? 'true' : 'false');

      var note = picked[mission.name]
        ? picked[mission.name] + (picked[mission.name] === 1 ? ' strap picked' : ' straps picked')
        : mission.colourways + ' colourways';

      button.innerHTML =
        (mission.thumb ? '<img class="bundle-watch-row__thumb" src="' + mission.thumb + '" alt="" width="40" height="40">' : '') +
        '<span class="bundle-watch-row__name">' + mission.name + '</span>' +
        '<span class="bundle-watch-row__note">' + note + '</span>';
      button.addEventListener('click', function () { self.setWatch(mission); });
      li.appendChild(button);
      self.el.watchList.appendChild(li);
    });
  };

  /* ---------------------------------------------------------------- cart */

  /*
    Control identifies an add-on's category by these exact strings and returns
    null for anything else, so a mistranslated role is silently dropped rather
    than rejected. 'clasp' is not 'spring-deployant-clasp'.
  */
  var CONTROL_CATEGORY = {
    buckle: 'classic-buckle',
    clasp: 'spring-deployant-clasp',
    tool: 'tools',
    storage: 'storage',
  };

  /**
   * The cart properties for one strap and its closure.
   *
   * Serialised by the theme's own ProductAddonsPanel, which is what the product
   * page uses, so the two cannot drift. That writes `_buckle` with the variant
   * id and `Buckle` with the display name, omits `_buckle` when the closure is
   * the one included with the strap, and packs `_ap_addons` for the cart
   * display. The cart transform then expands the line into a priced bundle.
   *
   * This page first sent `_addon_variant_ids` and a separate line per closure.
   * Neither is what the transform reads, so closures would have arrived as
   * loose products at their own prices.
   */
  Bundle.prototype.cartPropertiesFor = function (strap) {
    var properties = {};

    var selections = (strap.closure || []).map(function (choice) {
      return {
        categoryId: CONTROL_CATEGORY[choice.role] || choice.role || 'classic-buckle',
        variantId: choice.variantId,
        isFree: choice.isFree,
        title: choice.title,
        variantTitle: choice.variantTitle,
        imageUrl: choice.image,
      };
    });

    var control = window.ProductAddonsPanel;
    if (control && typeof control.applyComboComponentProperties === 'function') {
      control.applyComboComponentProperties(properties, selections);
    } else {
      // product-addons has not loaded. Write the same shape by hand rather than
      // adding the strap with no closure at all.
      selections.forEach(function (selection) {
        var isBuckle = selection.categoryId === 'classic-buckle' ||
          selection.categoryId === 'spring-deployant-clasp';
        if (selection.title) properties[isBuckle ? 'Buckle' : 'Storage'] = selection.title;
        if (!(selection.isFree && isBuckle)) {
          properties[isBuckle ? '_buckle' : '_storage'] = String(selection.variantId);
        }
      });
      if (window.slog) {
        window.slog('custom', { message: 'bundle serialised its cart properties without product-addons' });
      }
    }

    // What the bundle discount function matches on. It re-checks collection
    // membership and counts units itself, so this records intent only.
    properties._bundle = this.data.bundleKey;
    if (strap.size) properties.Size = strap.size;

    return properties;
  };

  Bundle.prototype.submit = function () {
    if (!this.straps.length) return;
    var self = this;

    var items = this.straps.map(function (strap) {
      return {
        id: strap.variantId,
        quantity: 1,
        properties: self.cartPropertiesFor(strap),
      };
    });

    this.el.submit.disabled = true;
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items }),
    })
      .then(function (r) {
        return r.json().catch(function () { return null; }).then(function (body) {
          if (!r.ok) {
            // Shopify puts the reason in `description`, and it names the line.
            var reason = (body && (body.description || body.message)) || '';
            var error = new Error(reason || 'cart rejected the bundle');
            error.status = r.status;
            throw error;
          }
          return body;
        });
      })
      .then(function () {
        /*
          Adds to the cart and shows it, rather than taking the shopper to
          checkout, so a bundle that took three choices to build is not thrown
          away by navigating off the page.

          product:added, not cart-drawer:open. cart-drawer:open is the drawer's
          pre-open hook: it unhides the element and arms the scroll lock, and
          then nothing slides it in, which is why the drawer measured 333px wide
          and stayed invisible. onProductAdded is the one that re-renders the
          cart from the server and calls drawer.openDrawer().

          Dispatched on document, which is where the drawer listens and where
          product-addons.js fires its own. GTM listens on document.body, and an
          event dispatched on document never reaches it, so nothing is double
          counted. Klaviyo reads detail.lineItem and returns without one.
        */
        if (self.data.layout === 'list') {
          self.el.submit.disabled = false;
          /*
            The tools upsell first, the cart behind it, which is the order the
            product page uses. If there is no upsell to show, the cart opens
            straight away instead.
          */
          self.openToolsUpsell().then(function (opened) {
            if (!opened) self.announceCartAdd(items.length);
          });
          return;
        }
        window.location.href = '/checkout';
      })
      .catch(function (error) {
        self.el.submit.disabled = false;
        /*
          This used to re-enable the button and log, and render nothing. The
          items go to /cart/add.js as one batch, so a single sold-out length
          takes the whole bundle down with a 422, and the shopper was left
          pressing a button that did nothing with no way to work out why.
        */
        self.showSubmitError(error);
        if (window.slog) {
          window.slog('custom', {
            message: 'bundle add to cart failed',
            extra: { error: String(error && error.message || error), status: Number(error && error.status) || 0 },
          });
        }
      });
  };

  /**
   * Hand the add off to the rest of the store.
   *
   * product:added, not cart-drawer:open. cart-drawer:open is the drawer's
   * pre-open hook: it unhides the element and arms the scroll lock, and then
   * nothing slides it in, which is why the drawer measured 333px wide and
   * stayed invisible. onProductAdded is the one that re-renders the cart from
   * the server and calls drawer.openDrawer().
   *
   * Dispatched on document, which is where the drawer listens and where
   * product-addons.js fires its own. GTM listens on document.body, and an event
   * dispatched on document never reaches it, so nothing is double counted.
   * Klaviyo reads detail.lineItem and returns without one.
   */
  Bundle.prototype.announceCartAdd = function (quantity) {
    document.dispatchEvent(new CustomEvent('product:added', {
      bubbles: true,
      detail: { source: 'bundle', quantity: quantity || 1 },
    }));
  };

  /**
   * Offer tools once the bundle is in the cart.
   *
   * Entered exactly the way the cart drawer's own "Want an add-on?" link is
   * entered: a trigger carrying the cart line's key, so the sheet attaches what
   * it sells to a line that is already in the cart instead of adding the strap
   * a second time. Nothing here reaches into either upsell's internals; the
   * bootstrap intercepts this click and routes it to the v2 sheet.
   *
   * The closure step is skipped rather than switched off after the fact: this
   * page asks that question at the rung, before anything reaches the cart.
   * Resolves false whenever there is nothing to show, which is the caller's cue
   * to open the cart itself.
   */
  Bundle.prototype.openToolsUpsell = function () {
    var self = this;
    if (!this.straps.length) return Promise.resolve(false);

    var wanted = this.straps[0].variantId;
    return fetch('/cart.js', {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin',
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        var line = ((cart && cart.items) || []).filter(function (item) {
          return Number(item.variant_id) === Number(wanted);
        })[0];
        if (!line || !line.handle) return false;

        var trigger = document.createElement('button');
        trigger.type = 'button';
        trigger.hidden = true;
        trigger.setAttribute('data-action', 'open-addons-drawer');
        trigger.setAttribute('data-product-handle', line.handle);
        trigger.setAttribute('data-parent-key', line.key);
        trigger.setAttribute('data-parent-variant-id', String(line.variant_id));
        trigger.setAttribute('data-parent-quantity', String(line.quantity));
        trigger.setAttribute('data-wb-skip-closure', 'true');
        document.body.appendChild(trigger);
        trigger.click();
        trigger.parentNode.removeChild(trigger);

        return self.awaitUpsell();
      })
      .catch(function () { return false; });
  };

  /**
   * Wait for whichever sheet the click opened.
   *
   * The bootstrap decides that, not this page, so this watches for either of
   * them rather than naming one. Resolving false means nothing came up, and the
   * caller opens the cart instead of leaving the shopper on a page that has
   * given them no sign their bundle went anywhere.
   */
  Bundle.prototype.awaitUpsell = function () {
    var self = this;
    var open = function () {
      var v2 = document.querySelector('[data-wb-root]');
      if (v2 && v2.getAttribute('aria-expanded') === 'true') return v2;
      var legacy = document.querySelector('#addons-drawer-mount product-addons-drawer');
      if (legacy && legacy.getAttribute('aria-expanded') === 'true') return legacy;
      return null;
    };

    return new Promise(function (resolve) {
      var tries = 0;
      var tick = function () {
        var el = open();
        if (el) {
          self.cartAfterUpsell(el);
          resolve(true);
          return;
        }
        tries += 1;
        if (tries > 60) { resolve(false); return; }
        setTimeout(tick, 100);
      };
      tick();
    });
  };

  /**
   * Show the cart once the upsell is done with.
   *
   * Adding tools opens the cart on its own. Closing the drawer without taking
   * any does not, and the shopper is then looking at the bundle page with no
   * sign of what they just bought, so this covers that path only.
   */
  Bundle.prototype.cartAfterUpsell = function (el) {
    var self = this;
    var observer = new MutationObserver(function () {
      if (el.getAttribute('aria-expanded') === 'true') return;
      observer.disconnect();
      var cart = document.querySelector('cart-drawer');
      if (cart && cart.getAttribute('aria-expanded') === 'true') return;
      self.announceCartAdd(self.straps.length);
    });
    observer.observe(el, { attributes: true, attributeFilter: ['aria-expanded'] });
  };

  /**
   * Say what went wrong, next to the button that failed.
   *
   * Rendered rather than alerted so it can name the reason Shopify gave, and
   * cleared on the next render so it cannot outlive the state that caused it.
   */
  Bundle.prototype.showSubmitError = function (error) {
    var panel = this.el.panel;
    if (!panel) return;
    var box = panel.querySelector('[data-bundle-error]');
    if (!box) {
      box = document.createElement('p');
      box.className = 'bundle-panel__error';
      box.setAttribute('data-bundle-error', '');
      box.setAttribute('role', 'alert');
      this.el.submit.parentNode.insertBefore(box, this.el.submit);
    }
    var labels = this.data.labels || {};
    var reason = String((error && error.message) || '');
    box.textContent = reason && reason !== 'cart rejected the bundle'
      ? reason
      : (labels.submitFailed || 'Something in this bundle is no longer available. Remove it and try again.');
    box.hidden = false;
  };

  Bundle.prototype.clearSubmitError = function () {
    var box = this.el.panel && this.el.panel.querySelector('[data-bundle-error]');
    if (box) box.hidden = true;
  };

  /* ---------------------------------------------------------------- wiring */

  Bundle.prototype.bind = function () {
    var self = this;

    // The inline script may have restored a density before this ran.
    var restored = this.root.getAttribute('data-density');
    if (restored) this.setDensity(restored);

    this.root.querySelectorAll('[data-bundle-sheet-close]').forEach(function (b) {
      b.addEventListener('click', function () { self.dismissSheet(); });
    });
    this.root.querySelectorAll('[data-bundle-watch-close]').forEach(function (b) {
      b.addEventListener('click', function () { self.closeWatchSheet(); });
    });
    this.root.querySelector('[data-bundle-change-watch]').addEventListener('click', function () {
      self.openWatchSheet();
    });
    if (this.el.watchSearch) {
      this.el.watchSearch.addEventListener('input', function () {
        self.renderWatchList(self.el.watchSearch.value);
      });
    }
    this.root.querySelectorAll('[data-bundle-viewer-close]').forEach(function (b) {
      b.addEventListener('click', function () { self.closeViewer(); });
    });
    this.bindViewerZoom();
    this.bindViewerSwipe();

    /*
      The viewer closes and the strap goes in.

      In the list design there is no add sheet: the picker row is the control
      and the default length is taken, so handing over to the sheet here put a
      size and closure step back in front of a shopper who had already been
      given neither. The grid design still has that sheet, so it still gets it.
    */
    this.el.viewerAdd.addEventListener('click', function () {
      var item = self.viewing;
      self.closeViewer();
      if (!item) return;
      if (self.data.layout === 'list') self.selectStrap(item.row);
      else self.openSheet(item.row);
    });

    /*
      Now that the picker floats over the box rather than growing it, a click
      anywhere else closes it, the way a menu does. Clicks on a slot are left
      alone: the slot's own handler is what opens and toggles it.
    */
    if (this.data.layout === 'list') {
      document.addEventListener('click', function (event) {
        if (typeof self.openSlot !== 'number') return;
        /*
          The path the click took, not where its target is now. A material chip
          or a size button rebuilds its own row, so the element clicked is
          detached by the time the click reaches the document, and closest()
          then answered "outside" and shut the picker on every filter.
        */
        var inside = event.composedPath().some(function (node) {
          return node.nodeType === 1 && node.matches('[data-bundle-picker], .bundle-panel__slot');
        });
        if (inside) return;
        self.openSlot = null;
        self.replaceIndex = null;
        self.parkPicker();
      });
    }

    /*
      The scrim closes it. The watch picker has a scrim button, this one is
      built out of a backdrop and a panel, so the backdrop is anything in the
      dialog that is not the panel.
    */
    if (this.el.viewer) {
      /*
        Judged on the path the click took, not on where its target is now.
        A thumbnail click re-renders the strip, so by the time the click
        reaches here the thumbnail is detached, closest() finds no panel, and
        picking a photograph closed the viewer.
      */
      this.el.viewer.addEventListener('click', function (event) {
        var panel = self.el.viewer.querySelector('.bundle-viewer__panel');
        if (panel && event.composedPath().indexOf(panel) !== -1) return;
        self.closeViewer();
      });
    }

    /*
      A swipe cannot be the only way in. The handle toggles, and a tap anywhere
      in the panel that is not a control opens it.
    */
    if (this.el.handle) {
      this.el.handle.addEventListener('click', function (event) {
        event.stopPropagation();
        self.holdOpen();
        self.setExpanded(!self.expanded);
      });
    }

    this.el.panel.addEventListener('click', function (event) {
      if (event.target.closest('button')) return;
      self.holdOpen();
      if (!self.expanded && self.straps.length) self.setExpanded(true);
    });

    /*
      Dragging the sheet.

      Touch events, not pointer events. The pointer version followed the finger
      in a desktop browser and did nothing at all on a phone: iOS decides part
      way through whether a gesture is a scroll, and when it decides yes it
      fires pointercancel, which arrived with no movement recorded and snapped
      the sheet back. touchmove with passive: false lets the move be claimed
      before that decision is made.

      No library for this. Hammer.js is about 20kB to replace forty lines, it
      has been unmaintained for years, and this theme has no bundler.
    */
    var COLLAPSED_MIN = 150;
    var EXPANDED = 375;
    var drag = null;

    var grabbable = function (target) {
      if (!target || !target.closest) return false;
      if (target.closest('button') && !target.closest('[data-bundle-handle]')) return false;
      return !!(target.closest('[data-bundle-handle]') || target.closest('.bundle-panel__summary'));
    };

    var begin = function (y, target) {
      if (!grabbable(target)) return false;
      if (!self.straps.length) return false;
      drag = {
        y: y,
        from: self.el.panel.getBoundingClientRect().height,
        moved: 0,
        t: Date.now(),
        claimed: false,
      };
      self.el.panel.setAttribute('data-dragging', 'true');
      return true;
    };

    var move = function (y) {
      if (!drag) return false;
      drag.moved = drag.y - y;
      // Under a few pixels it is still a tap, so the page keeps the gesture.
      if (!drag.claimed && Math.abs(drag.moved) < 6) return false;
      drag.claimed = true;
      var next = Math.min(EXPANDED, Math.max(COLLAPSED_MIN, drag.from + drag.moved));
      self.el.panel.style.height = next + 'px';
      return true;
    };

    var end = function () {
      if (!drag) return;
      var moved = drag.moved;
      var claimed = drag.claimed;
      var quick = (Date.now() - drag.t) < 300 && Math.abs(moved) > 10;
      drag = null;

      self.el.panel.removeAttribute('data-dragging');
      self.el.panel.style.height = '';

      if (!claimed) return;    // a tap; the click handler deals with it
      self.holdOpen();
      if (quick) self.setExpanded(moved > 0);
      else self.setExpanded(Math.abs(moved) > 70 ? moved > 0 : self.expanded);
    };

    this.el.panel.addEventListener('touchstart', function (event) {
      if (event.touches.length !== 1) return;
      begin(event.touches[0].clientY, event.target);
    }, { passive: true });

    this.el.panel.addEventListener('touchmove', function (event) {
      if (!drag || event.touches.length !== 1) return;
      // Claim the gesture before the browser decides it is a scroll.
      if (move(event.touches[0].clientY) && event.cancelable) event.preventDefault();
    }, { passive: false });

    this.el.panel.addEventListener('touchend', end);
    this.el.panel.addEventListener('touchcancel', end);

    // Mouse, for the desktop rail and for testing.
    this.el.panel.addEventListener('mousedown', function (event) {
      if (event.button !== 0) return;
      if (!begin(event.clientY, event.target)) return;
      var onMove = function (e) { move(e.clientY); };
      var onUp = function () {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        end();
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    });

    /*
      How many straps to a row. The attribute lives on the section root, which
      is where the inline restore script put the stored choice before the grid
      existed, so the button and the restore agree on one place.
    */
    this.root.querySelectorAll('[data-bundle-density]').forEach(function (button) {
      button.addEventListener('click', function () {
        self.setDensity(button.getAttribute('data-bundle-density'));
      });
    });

    this.bindSearch();

    /*
      In the list design the row is the control. Delegated from the grid rather
      than bound per card, so it covers the server rendered tiles and the ones
      the script builds, and survives every re-render without rebinding.
    */
    if (this.data.layout === 'list' && this.el.grid) {
      this.el.grid.addEventListener('click', function (event) {
        var card = event.target.closest ? event.target.closest('.bundle-card') : null;
        if (!card) return;
        // The photograph still opens the viewer, and the remove still removes.
        var see = event.target.closest('[data-bundle-see]');
        if (see) {
          event.preventDefault();
          var seeId = Number(card.getAttribute('data-bundle-variant'));
          var seeRow = (self.catalogue.allRows || []).filter(function (r) { return r.v === seeId; })[0];
          var seeItem = seeRow ? self.itemForRow(seeRow) : null;
          if (seeItem) self.openViewer(seeItem);
          return;
        }
        if (event.target.closest('.bundle-card__photo, .bundle-card__zoom, .bundle-stepper')) return;
        var id = Number(card.getAttribute('data-bundle-variant'));
        var row = (self.catalogue.allRows || []).filter(function (r) { return r.v === id; })[0];
        if (row) self.selectStrap(row);
      });
    }

    /*
      The payment count opens the rest, and closes them again. It used to delete
      itself on the way open, so sixteen logos took over the bottom of the box
      with no way back to five.
    */
    var morePayments = this.root.querySelector('[data-bundle-more-payments]');
    if (morePayments) {
      var moreLabel = morePayments.querySelector('[data-more-label]') || morePayments;
      var moreText = moreLabel.textContent;
      var moreAria = morePayments.getAttribute('aria-label') || '';
      morePayments.addEventListener('click', function () {
        var labels = self.data.labels || {};
        var opening = morePayments.getAttribute('aria-expanded') !== 'true';
        self.root.querySelectorAll('.bundle-trust__method--extra').forEach(function (li) {
          li.hidden = !opening;
        });
        morePayments.setAttribute('aria-expanded', opening ? 'true' : 'false');
        moreLabel.textContent = opening ? (labels.lessPayments || 'Show less') : moreText;
        morePayments.setAttribute('aria-label', opening
          ? (labels.lessPaymentsLabel || 'Show fewer payment methods')
          : moreAria);
      });
    }

    this.el.confirm.addEventListener('click', function () { self.confirmPending(); });
    this.el.submit.addEventListener('click', function () { self.submit(); });

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      if (self.el.sheet.hasAttribute('open')) self.dismissSheet();
      else if (self.el.viewer.hasAttribute('open')) self.closeViewer();
      else if (self.el.watchSheet.hasAttribute('open')) self.closeWatchSheet();
    });
  };

  function start() {
    document.querySelectorAll('[data-bundle-root][data-bundle-mode="collection"]').forEach(function (root) {
      if (root.__bundle) return;
      try {
        root.__bundle = new Bundle(root);
      } catch (error) {
        if (window.slog) {
          window.slog('custom', { message: 'bundle collection failed to start', extra: { error: String(error) } });
        }
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  // Exposed so the parsing rules can be checked without a browser.
  window.WBBundleParse = { missionOf: missionOf, bareColour: bareColour, materialOf: materialOf, missionKey: missionKey };
}());

